
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import {
  buildGeneratePayload,
  buildAttributeList,
  buildHighlightList,
  buildPreviewImages,
  extractSkuInsights,
  mapStageStatusToAgentStatus,
  normalizeImagePlans,
  resolveApiBaseUrl,
  resolveAuthToken,
  resolveStudioCollectionId,
  splitLogMessageParts,
  toStageStatusMap,
  type AgentStatus,
  type ImagePlan,
  type StageStatusMap
} from '../lib/utils';
import {
  AutoFixHigh,
  CheckCircle,
  ManageSearch,
  AutoAwesome,
  Hub,
  Sync,
  SmartToy,
  MoreVert,
  Dns,
  Palette,
  Search,
  Visibility,
  EditNote,
  RocketLaunch,
  Link as LinkIcon,
  Image as ImageIcon,
  VerifiedUser,
  ChevronLeft,
  ChevronRight,
  StopCircle,
  Architecture,
  UnfoldLess,
  UnfoldMore,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Biotech,
  CloudDownload
} from '@mui/icons-material';

// --- Types ---
type StageStatusResponse = {
  stages?: StageStatusMap | null;
  completed?: string[] | null;
  failed?: string | null;
  failed_error?: string | null;
  thinking?: Record<string, string[]>;
};

type AgentLogEvent = {
  seq: number;
  event_type: string;
  message: string;
  created_at?: string | null;
};

type AgentLogRun = {
  run_id: string;
  agent_name: string;
  stage: string;
  status: string;
  started_at?: string | null;
  completed_at?: string | null;
  events: AgentLogEvent[];
};

type CollectionPreview = {
  collection_id?: string;
  title?: string;
  images?: Array<{ storage_path?: string; url?: string; original_url?: string; r2_url?: string }>;
  extracted_data?: Record<string, any>;
  generated_content?: Record<string, any> | null;
  rich_content?: Record<string, any> | null;
  upload_data?: Record<string, any> | null;
  image_extracted_text?: { text?: string; image_count?: number } | null;
  unified_listing?: Record<string, any> | null;
  seo_keyword_graph?: Record<string, any> | null;
  listing_output?: Record<string, any> | null;
  visual_strategy?: Record<string, any> | null;
  quality_report?: Record<string, any> | null;
  adapter_output?: Record<string, any> | null;
  export_data?: Record<string, any> | null;
};

export type StudioDemoState = {
  preview?: CollectionPreview | null;
  stageStatus?: StageStatusMap;
  completedStages?: string[];
  imagePlans?: ImagePlan[];
  agentRuns?: AgentLogRun[];
  selectedStage?: string;
  marketStatus?: string;
  skipImages?: boolean;
  actionMessage?: string;
  actionError?: string;
  isGenerating?: boolean;
  isExecutingStage?: boolean;
  collectionId?: string;
  loadError?: string;
};

type StudioScreenProps = {
  demoState?: StudioDemoState;
};

const CORE_AGENT_STAGES = [
  'info_structure',
  'seo',
  'listing',
  'visual',
  'adapter',
  'export'
];

const STAGE_OPTIONS = [
  { value: 'info_structure', labelKey: 'studio.stage.productUnderstanding' },
  { value: 'seo', labelKey: 'studio.stage.seo' },
  { value: 'listing', labelKey: 'studio.stage.listing' },
  { value: 'visual', labelKey: 'studio.stage.visual' },
  { value: 'adapter', labelKey: 'studio.stage.adapter' },
  { value: 'export', labelKey: 'studio.stage.export' }
];
const TOTAL_STAGE_COUNT = CORE_AGENT_STAGES.length;

const MARKET_STATUS_OPTIONS = [
  { value: '中等竞争', labelKey: 'studio.market.medium' },
  { value: '高竞争', labelKey: 'studio.market.high' },
  { value: '低竞争', labelKey: 'studio.market.low' }
];

// --- Helper Components for Animations ---

const ScanningOverlay: React.FC<{ active: boolean }> = ({ active }) => {
  const { t } = useLanguage();
  if (!active) return null;
  return (
    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-md">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-400/20 to-transparent w-full h-[30%] animate-[scan_2s_linear_infinite]" style={{ borderBottom: '1px solid rgba(74, 222, 128, 0.5)' }}></div>
      <div className="absolute top-2 right-2 bg-green-500/90 text-white text-[8px] font-mono px-1.5 py-0.5 rounded shadow-sm animate-pulse">
        {t('studio.source.scanning')}
      </div>
    </div>
  );
};

const DecodingText: React.FC<{ text: string; reveal: boolean }> = ({ text, reveal }) => {
  return (
    <span className={`transition-all duration-1000 ${reveal ? 'text-slate-900 dark:text-slate-200 blur-0' : 'text-slate-400 blur-[2px] select-none'}`}>
      {reveal ? text : '••••••••••••••••••••••••••••'}
    </span>
  );
};

const DiffusionCanvas: React.FC<{ active: boolean }> = ({ active }) => {
  const { t } = useLanguage();
  if (!active) return null;
  return (
    <div className="absolute inset-0 z-10 rounded-lg overflow-hidden bg-slate-900/10 backdrop-blur-[1px]">
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent w-[200%] h-full animate-[shimmer_2s_infinite] -translate-x-full"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-blue-600/90 backdrop-blur text-white text-[9px] font-bold px-3 py-1 rounded-full border border-blue-400/50 shadow-lg tracking-wider flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-[ping_1s_infinite]"></span>
          {t('studio.gallery.diffusing')}
        </div>
      </div>
    </div>
  );
};

const ShimmerSheen: React.FC = () => (
  <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden rounded-lg">
    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent w-[200%] h-full animate-[sheen_4s_infinite_delay-1s] -translate-x-[150%] skew-x-12"></div>
  </div>
);

const formatAgentTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatAgentDuration = (start?: string | null, end?: string | null) => {
  if (!start) return '';
  const startTime = new Date(start).getTime();
  if (Number.isNaN(startTime)) return '';
  const endTime = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(endTime)) return '';
  const seconds = Math.max(0, (endTime - startTime) / 1000);
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds % 60);
  return `${minutes}m ${rem}s`;
};

const VisualPlanRow: React.FC<{
  badge: string;
  title: string;
  subtitle: string;
  prompt: string;
  keywords?: string[];
  scene?: string;
  status: 'complete' | 'processing' | 'waiting';
  isExpanded: boolean;
  onToggle: () => void;
  editablePrompt?: string;
  onPromptChange?: (value: string) => void;
  onSavePrompt?: () => void;
  onGenerate?: () => void;
  onRegenerate?: () => void;
  generatedUrl?: string;
}> = ({
  badge,
  title,
  subtitle,
  prompt,
  keywords,
  scene,
  status,
  isExpanded,
  onToggle,
  editablePrompt,
  onPromptChange,
  onSavePrompt,
  onGenerate,
  onRegenerate,
  generatedUrl
}) => {
  const { t } = useLanguage();
  return (
    <div className={`transition-all duration-300 border-b border-slate-100 ${isExpanded ? 'bg-slate-50/50' : 'bg-white hover:bg-slate-50'}`}>
      <div
        className="px-8 py-5 flex items-center cursor-pointer group select-none"
        onClick={onToggle}
      >
        <div className="shrink-0 w-20 mr-4">
          <span className={`inline-flex items-center justify-center text-[9px] font-black px-2.5 py-1.5 rounded uppercase tracking-wider ${status === 'processing' ? 'bg-blue-600 text-white' : 'bg-slate-900 text-white'}`}>
            {badge}
          </span>
        </div>
        <div className="w-1/3 min-w-[220px]">
          <h4 className="text-sm font-bold text-slate-900 leading-tight">{title}</h4>
          <p className="text-[11px] text-slate-500 mt-1 italic">{subtitle}</p>
        </div>
        <div className="flex-1"></div>
        <div className="shrink-0 flex items-center gap-6">
          {status === 'complete' && <CheckCircle className="text-emerald-500" style={{ fontSize: 22 }} />}
          {status === 'processing' && <Sync className="text-blue-600 animate-spin" style={{ fontSize: 22 }} />}
          {status === 'waiting' && <MoreVert className="text-slate-300" style={{ fontSize: 22 }} />}

          <div className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200/50 transition-colors">
            {isExpanded ? (
              <ExpandLess className="text-slate-400 text-xl" />
            ) : (
              <ExpandMore className="text-slate-400 text-xl group-hover:text-slate-600 transition-colors" />
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-8 pb-6 pl-[7rem] animate-in fade-in slide-in-from-top-1 duration-200">
          {(scene || (keywords && keywords.length > 0)) && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[9px]">
              {scene && (
                <span className="px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-600 font-semibold">
                  {t('studio.visual.sceneLabel')}: {scene}
                </span>
              )}
              {keywords?.map((keyword) => (
                <span
                  key={keyword}
                  className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 font-semibold"
                >
                  {keyword}
                </span>
              ))}
            </div>
          )}
          <div className="bg-slate-50/50 p-5 rounded-xl border border-slate-200/60 relative group/prompt shadow-sm">
            <div className="absolute top-3 right-3">
              <button className="p-2 rounded-lg hover:bg-slate-200/50 text-slate-400 hover:text-blue-600 transition-colors">
                <ContentCopy style={{ fontSize: 18 }} />
              </button>
            </div>
            <code className="text-xs font-mono text-slate-600 leading-relaxed block pr-12">
              {prompt}
            </code>
            {generatedUrl && (
              <div className="mt-3 text-[10px] text-blue-600 font-semibold">
                {t('studio.visual.generated')}: {generatedUrl}
              </div>
            )}
          </div>
          {onPromptChange && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <textarea
                value={editablePrompt ?? prompt}
                onChange={(event) => onPromptChange(event.target.value)}
                className="h-24 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {onSavePrompt && (
                  <button
                    onClick={onSavePrompt}
                    className="rounded-lg bg-slate-800 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-slate-900"
                  >
                    {t('studio.visual.savePrompt')}
                  </button>
                )}
                {onGenerate && (
                  <button
                    onClick={onGenerate}
                    className="rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700"
                  >
                    {t('studio.visual.generateImage')}
                  </button>
                )}
                {onRegenerate && (
                  <button
                    onClick={onRegenerate}
                    className="rounded-lg bg-amber-500 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-600"
                  >
                    {t('studio.visual.regenerate')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Agent Module Container ---
const AgentModule: React.FC<{
  status: AgentStatus;
  themeColor: 'blue' | 'emerald' | 'amber' | 'cyan' | 'pink' | 'red';
  className?: string;
  header: React.ReactNode;
  children: React.ReactNode;
}> = ({ status, themeColor, className = '', header, children }) => {

  // Define gradient colors based on theme and status
  const getGradientColors = () => {
    if (status === 'COMPLETE') return 'from-green-400 via-green-300 to-transparent'; // Success Green
    if (status === 'ERROR') return 'from-red-500 via-red-600 to-transparent'; // Warning Red

    switch (themeColor) {
      case 'blue': return 'from-blue-500 via-indigo-500 to-transparent';
      case 'emerald': return 'from-emerald-500 via-teal-500 to-transparent';
      case 'amber': return 'from-amber-500 via-orange-500 to-transparent';
      case 'cyan': return 'from-cyan-500 via-sky-500 to-transparent';
      case 'pink': return 'from-pink-500 via-rose-500 to-transparent';
      case 'red': return 'from-red-500 via-orange-500 to-transparent';
      default: return 'from-slate-300 via-slate-400 to-transparent';
    }
  };

  const isAnimating = status !== 'IDLE';
  const gradientClass = getGradientColors();

  const animationClass = status === 'COMPLETE'
    ? ''
    : status === 'ERROR'
      ? 'animate-[jitter_0.3s_ease-in-out_3]' // Limit to 3 shakes
      : 'animate-[spin_3s_linear_infinite]';

  // Environmental Glow Logic
  const getGlowStyle = () => {
    if (status === 'COMPLETE') return 'bg-green-400/15 blur-2xl';
    if (status === 'ERROR') return 'bg-red-500/20 blur-xl';
    if (status === 'ACTIVE' || status === 'THINKING') {
      switch (themeColor) {
        case 'blue': return 'bg-blue-500/15 blur-2xl animate-pulse';
        case 'amber': return 'bg-orange-500/15 blur-2xl animate-pulse';
        case 'emerald': return 'bg-emerald-500/15 blur-2xl animate-pulse';
        case 'cyan': return 'bg-cyan-500/15 blur-2xl animate-pulse';
        case 'pink': return 'bg-pink-500/15 blur-2xl animate-pulse';
        case 'red': return 'bg-red-500/15 blur-2xl animate-pulse';
        default: return 'bg-slate-500/15 blur-2xl animate-pulse';
      }
    }
    return '';
  };

  return (
    <div className={`relative rounded-xl z-0 group ${className} ${status === 'ERROR' ? 'animate-[jitter_0.3s_ease-in-out_3]' : ''}`}>
      {/* 1. Environmental Status Glow (The "Aura") */}
      {status !== 'IDLE' && (
        <div className={`absolute -inset-4 rounded-3xl z-[-2] transition-all duration-1000 ${getGlowStyle()}`}></div>
      )}

      {/* 2. Animated Border Layer (The "Rim") */}
      {isAnimating && status !== 'COMPLETE' && (
        <div className="absolute -inset-[1px] rounded-xl overflow-hidden z-[-1]">
          <div className={`absolute inset-[-50%] bg-[conic-gradient(from_0deg_at_50%_50%,transparent_0deg,transparent_100deg,var(--tw-gradient-via)_180deg,transparent_180deg,transparent_280deg,var(--tw-gradient-via)_360deg)] ${gradientClass} ${animationClass} opacity-100`}></div>
        </div>
      )}

      {/* 3. Background/Content Layer */}
      <div className={`bg-white rounded-xl w-full overflow-hidden relative ${status === 'IDLE' ? 'border border-slate-200 shadow-sm' : status === 'ERROR' ? 'border border-red-300 shadow-red-100' : 'border border-transparent bg-clip-padding'}`}>
        {status === 'COMPLETE' && <div className="absolute inset-0 bg-green-50/10 pointer-events-none"></div>}
        {status === 'ERROR' && <div className="absolute inset-0 bg-red-50/10 pointer-events-none mix-blend-overlay"></div>}

        {/* Header Section */}
        <div className={`p-3 border-b border-slate-100 flex justify-between items-center ${status === 'ERROR' ? 'bg-red-50/30' :
          themeColor === 'blue' ? 'bg-slate-50/50' : `bg-${themeColor}-50/10`
          }`}>
          {header}
        </div>

        {/* Body Content */}
        {children}
      </div>
    </div>
  );
};

// --- Main Component ---

export const StudioScreen: React.FC<StudioScreenProps> = ({ demoState }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [_videoEnabled, _setVideoEnabled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [previewState, setPreview] = useState<CollectionPreview | null>(null);
  const [stageStatusState, setStageStatus] = useState<StageStatusMap>({});
  const [completedStagesState, setCompletedStages] = useState<string[]>([]);
  const [failedStage, setFailedStage] = useState<string | null>(null);
  const [loadErrorState, setLoadError] = useState('');
  const [imagePlansState, setImagePlans] = useState<ImagePlan[]>([]);
  const [promptDrafts, setPromptDrafts] = useState<Record<number, string>>({});
  const [isGeneratingState, setIsGenerating] = useState(false);
  const [isExecutingStageState, setIsExecutingStage] = useState(false);
  const [selectedStageState, setSelectedStage] = useState(STAGE_OPTIONS[0]?.value ?? '');
  const [marketStatusState, setMarketStatus] = useState(MARKET_STATUS_OPTIONS[0]?.value ?? '');
  const [skipImagesState, setSkipImages] = useState(false);
  const [actionMessageState, setActionMessage] = useState('');
  const [actionErrorState, setActionError] = useState('');
  const [agentRunsState, setAgentRuns] = useState<AgentLogRun[]>([]);
  const [agentLogError, setAgentLogError] = useState('');
  const [expandedAgentStages, setExpandedAgentStages] = useState<Set<string>>(new Set());
  const [expandedLogEntries, setExpandedLogEntries] = useState<Set<string>>(new Set());
  const agentLogRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const agentAutoExpandRef = useRef(false);
  const animatedLogKeysRef = useRef<Set<string>>(new Set());

  const demo = demoState;
  const isDemo = Boolean(demo);
  const preview = demo?.preview ?? previewState;
  const stageStatus = demo?.stageStatus ?? stageStatusState;
  const completedStages = demo?.completedStages ?? completedStagesState;
  const imagePlans = demo?.imagePlans ?? imagePlansState;
  const agentRuns = demo?.agentRuns ?? agentRunsState;
  const selectedStage = demo?.selectedStage ?? selectedStageState;
  const marketStatus = demo?.marketStatus ?? marketStatusState;
  const skipImages = demo?.skipImages ?? skipImagesState;
  const actionMessage = demo?.actionMessage ?? actionMessageState;
  const actionError = demo?.actionError ?? actionErrorState;
  const isGenerating = demo?.isGenerating ?? isGeneratingState;
  const isExecutingStage = demo?.isExecutingStage ?? isExecutingStageState;
  const loadError = demo?.loadError ?? loadErrorState;

  const apiBaseUrl = resolveApiBaseUrl();
  const authToken = resolveAuthToken();
  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {}
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`
    }
    return headers
  }, [authToken]);
  const collectionId = useMemo(
    () => resolveStudioCollectionId(location.state as Record<string, unknown> | null, location.search),
    [location.state, location.search]
  );
  const resolvedCollectionId = demo?.collectionId ?? collectionId;
  const selectedStageLabel = useMemo(() => {
    const match = STAGE_OPTIONS.find((option) => option.value === selectedStage);
    return match ? t(match.labelKey) : selectedStage;
  }, [selectedStage, t]);

  const previewImages = useMemo(() => buildPreviewImages(preview ?? {}), [preview]);
  const thumbnailImages = previewImages.slice(0, 5);
  const mainPreviewImage = previewImages[0] ?? null;
  const thumbnailSlots = useMemo(
    () => (thumbnailImages.length ? thumbnailImages : Array.from({ length: 5 }, () => null)),
    [thumbnailImages]
  );
  const extractedData = preview?.extracted_data ?? {};
  const unifiedListing = preview?.unified_listing ?? null;
  const seoKeywordGraph = preview?.seo_keyword_graph ?? unifiedListing?.seo ?? null;
  const listingOutput = preview?.listing_output ?? unifiedListing?.listing ?? null;
  const visualStrategy = preview?.visual_strategy ?? unifiedListing?.visual_strategy ?? null;
  const adapterOutput = preview?.adapter_output ?? null;
  const exportData = preview?.export_data ?? null;
  const productCore = (unifiedListing?.product_core ?? {}) as Record<string, any>;
  const productCoreTitle = String(productCore?.title ?? '').trim();
  const productCoreBrand = String(productCore?.brand ?? '').trim();
  const imageExtractedText = String(
    unifiedListing?.assets?.ocr_text ?? preview?.image_extracted_text?.text ?? ''
  );
  const previewTitle = productCoreTitle || extractedData?.title || preview?.title || '—';
  const previewBrand = productCoreBrand || extractedData?.brand || extractedData?.brand_name || '—';
  const previewCategory = extractedData?.category ?? extractedData?.category_name ?? '—';
  const attributeList = useMemo(() => buildAttributeList(extractedData), [extractedData]);
  const highlightList = useMemo(() => buildHighlightList(extractedData), [extractedData]);
  const highlightFallback = useMemo(
    () => highlightList.map((item) => ({ label: item.title, value: item.detail })),
    [highlightList]
  );
  const attributeDisplay = attributeList.length ? attributeList : highlightFallback;
  const highlightDisplay = useMemo(
    () =>
      highlightList.length
        ? highlightList
        : attributeDisplay.map((item) => ({ title: item.label, detail: item.value })),
    [attributeDisplay, highlightList]
  );
  const productType = String(extractedData?.product_type ?? extractedData?.productType ?? '').trim();
  const rawPageText = String(
    unifiedListing?.assets?.raw_text
      ?? extractedData?.raw_page_text
      ?? extractedData?.raw_text
      ?? extractedData?.raw_sections?.raw_text
      ?? ''
  );
  const hasRawText = Boolean(rawPageText.trim());
  const skuInsights = useMemo(() => extractSkuInsights(extractedData), [extractedData]);
  const priceLabel = useMemo(() => {
    if (extractedData?.price_range) {
      return String(extractedData.price_range);
    }
    if (skuInsights.price !== null && skuInsights.price !== undefined) {
      return String(skuInsights.price);
    }
    if (extractedData?.price) {
      return String(extractedData.price);
    }
    return '—';
  }, [extractedData, skuInsights.price]);
  const rawSectionEntries = useMemo(() => {
    const sections = extractedData?.raw_sections;
    if (!sections || typeof sections !== 'object') {
      return [];
    }
    return Object.entries(sections)
      .map(([key, value]) => {
        const text = String(value ?? '').trim();
        return {
          key: String(key),
          preview: text.slice(0, 140),
          length: text.length
        };
      })
      .filter((item) => item.preview)
      .slice(0, 4);
  }, [extractedData]);
  const skuCount = Array.isArray(extractedData?.parsed_skus) ? extractedData.parsed_skus.length : 0;
  const hasPreviewImages = previewImages.length > 0;
  const hasExtractedData = Object.keys(extractedData || {}).length > 0;
  const hasImageExtractedText = Boolean(imageExtractedText && imageExtractedText.trim());
  const visualPlans = useMemo(() => {
    if (imagePlans.length) {
      return imagePlans;
    }
    if (visualStrategy?.prompt_plans?.length) {
      return visualStrategy.prompt_plans.map((plan: Record<string, any>, index: number) => ({
        index: plan.index ?? index + 1,
        prompt: plan.prompt,
        image_type: plan.image_type ?? plan.scene,
        keywords: Array.isArray(plan.keywords) ? plan.keywords : [],
        scene: plan.scene,
        generated_url: plan.generated_url,
        aspect_ratio: plan.aspect_ratio
      }));
    }
    return [];
  }, [imagePlans, visualStrategy]);
  const generatedImages = useMemo(
    () => visualPlans.map((plan) => plan.generated_url).filter((url): url is string => Boolean(url)),
    [visualPlans]
  );
  const planStatusText = useMemo(() => {
    if (!visualPlans.length) {
      return t('studio.visual.statusWaiting');
    }
    if (!generatedImages.length) {
      return t('studio.visual.statusPrompted', { count: visualPlans.length });
    }
    return t('studio.visual.statusGenerated', { current: generatedImages.length, total: visualPlans.length });
  }, [generatedImages.length, t, visualPlans.length]);
  const trackOnePlans = useMemo(() => visualPlans.slice(0, 5), [visualPlans]);
  const trackTwoPlans = useMemo(() => visualPlans.slice(5), [visualPlans]);
  const trackOneExtraCount = Math.max(0, trackOnePlans.length - 1);
  const trackTwoExtraCount = Math.max(0, trackTwoPlans.length - 1);
  const visualConstraints = useMemo(
    () => (Array.isArray(visualStrategy?.constraints) ? visualStrategy?.constraints : []),
    [visualStrategy]
  );
  const visualKeywordPool = useMemo(() => {
    const set = new Set<string>();
    visualPlans.forEach((plan) => {
      const keywords = (plan as { keywords?: string[] }).keywords;
      if (Array.isArray(keywords)) {
        keywords.forEach((keyword) => {
          if (keyword && keyword.trim()) {
            set.add(keyword.trim());
          }
        });
      }
    });
    return Array.from(set).slice(0, 10);
  }, [visualPlans]);

  const workflowState = useMemo(() => {
    const sourceStatus = mapStageStatusToAgentStatus(stageStatus.info_structure?.status);
    const seoStatus = mapStageStatusToAgentStatus(stageStatus.seo?.status);
    const listingStatus = mapStageStatusToAgentStatus(stageStatus.listing?.status);
    const visualStatus = mapStageStatusToAgentStatus(stageStatus.visual?.status);
    const adapterStatus = mapStageStatusToAgentStatus(stageStatus.adapter?.status);
    const exportStatus = mapStageStatusToAgentStatus(stageStatus.export?.status);

    const completedCoreStages = CORE_AGENT_STAGES.filter((stage) => completedStages.includes(stage)).length;
    const progress = TOTAL_STAGE_COUNT
      ? Math.min(100, Math.round((completedCoreStages / TOTAL_STAGE_COUNT) * 100))
      : 0;

    return {
      source: sourceStatus,
      seo: seoStatus,
      listing: listingStatus,
      visual: visualStatus,
      adapter: adapterStatus,
      export: exportStatus,
      progress
    };
  }, [completedStages, stageStatus]);

  const keywordCards = useMemo(() => {
    const graph = (seoKeywordGraph as { keyword_graph?: { nodes?: Array<Record<string, any>> } })?.keyword_graph;
    const nodes = Array.isArray(graph?.nodes) ? graph?.nodes : [];
    const candidates = (seoKeywordGraph as { candidates?: Array<Record<string, any>> } | null)?.candidates ?? [];
    const source = Array.isArray(candidates) && candidates.length > 0 ? candidates : nodes;
    return source
      .map((item) => {
        const keyword = String(item?.keyword ?? item?.kw ?? '').trim();
        const intent = String(item?.intent ?? '').trim();
        const scoreValue = item?.score !== undefined ? Number(item.score) : undefined;
        const volumeRange = String(item?.volume_range ?? item?.volumeRange ?? '').trim();
        return {
          keyword,
          meaning: String(item?.meaning ?? '').trim(),
          searchVolume:
            volumeRange
            || (scoreValue !== undefined && !Number.isNaN(scoreValue) ? `${Math.round(scoreValue * 100)}` : ''),
          competition: intent,
          bucket: intent || 'conversion',
          cluster: String(item?.cluster ?? item?.cluster_tag ?? item?.clusterTag ?? '').trim(),
          volumeTier: String(item?.volume_tier ?? item?.volumeTier ?? '').trim(),
          volumeRange
        };
      })
      .filter((item) => item.keyword);
  }, [seoKeywordGraph]);

  const seoScoreTarget = useMemo(() => {
    if (!keywordCards.length) {
      return 0;
    }
    return Math.min(100, keywordCards.length * 10);
  }, [keywordCards.length]);

  const searchTermText = useMemo(() => {
    return keywordCards.map((item) => item.keyword).join(', ');
  }, [keywordCards]);
  const coverageReport =
    (seoKeywordGraph as { coverage_report?: Record<string, any> } | null)?.coverage_report
    ?? (unifiedListing?.seo as Record<string, any> | null)?.coverage_report
    ?? {};
  const seoMissingAspects = Array.isArray(coverageReport?.missing_aspects) ? coverageReport.missing_aspects : [];
  const seoDuplicates = Array.isArray(coverageReport?.duplicates) ? coverageReport.duplicates : [];
  const seoLanguageIssues = Array.isArray(coverageReport?.language_issues) ? coverageReport.language_issues : [];
  const listingKeywordHints = useMemo(
    () => keywordCards.map((item) => item.keyword).slice(0, 6),
    [keywordCards]
  );

  const listingTitle = String(listingOutput?.title ?? '').trim();
  const listingShort = String(listingOutput?.short_desc ?? '').trim();
  const listingLong = String(listingOutput?.long_desc ?? '').trim();
  const listingBullets = Array.isArray(listingOutput?.bullet_points) ? listingOutput?.bullet_points : [];
  const listingFaq = Array.isArray(listingOutput?.faq) ? listingOutput?.faq : [];
  const listingHasContent = Boolean(
    listingTitle || listingShort || listingLong || listingBullets.length || listingFaq.length
  );

  const adapterPlatform = String(adapterOutput?.platform ?? '').trim();
  const adapterPayload = (adapterOutput?.payload ?? {}) as Record<string, any>;
  const adapterPayloadPreview = useMemo(() => {
    try {
      return JSON.stringify(adapterPayload, null, 2);
    } catch {
      return '';
    }
  }, [adapterPayload]);
  const uploadAttributes = useMemo(() => {
    const payload = adapterOutput?.payload ?? {};
    if (!payload || typeof payload !== 'object') {
      return [] as Array<{ label: string; value: string }>;
    }
    const attributes = (payload as Record<string, any>).attributes ?? payload;
    if (Array.isArray(attributes)) {
      return attributes
        .filter((item) => item && typeof item === 'object')
        .map((item) => ({
          label: String(item.name ?? item.key ?? item.label ?? ''),
          value: String(item.value ?? item.default ?? '')
        }))
        .filter((item) => item.label && item.value);
    }
    if (typeof attributes === 'object') {
      return Object.entries(attributes)
        .map(([key, value]) => ({ label: String(key), value: String(value ?? '') }))
        .filter((item) => item.label && item.value);
    }
    return [] as Array<{ label: string; value: string }>;
  }, [adapterOutput]);
  const adapterFieldCount = Object.keys(adapterPayload).length;
  const adapterMissingFields = Array.isArray(adapterOutput?.missing_fields) ? adapterOutput?.missing_fields : [];
  const adapterError = String(adapterOutput?.error ?? '').trim();
  const uploadData = preview?.upload_data ?? null;
  const richContent = preview?.rich_content ?? null;
  const adapterCategoryId = adapterPayload?.description_category_id ?? adapterPayload?.category_id ?? adapterPayload?.category;
  const adapterTypeId = adapterPayload?.type_id ?? adapterPayload?.typeId;
  const adapterPrice = adapterPayload?.price ?? adapterPayload?.min_price ?? adapterPayload?.old_price;
  const adapterImageCount = Array.isArray(adapterPayload?.images)
    ? adapterPayload.images.length
    : Array.isArray(uploadData?.images)
      ? uploadData?.images.length
      : uploadData?.image_generated ?? 0;
  const adapterAttributeCount = uploadAttributes.length;
  const richContentWidgetCount = Array.isArray(richContent?.widgets)
    ? richContent.widgets.length
    : Array.isArray(richContent?.content)
      ? richContent.content.length
      : 0;

  const exportFormat = String(exportData?.format ?? '').trim();
  const exportTemplateVersion = String(exportData?.template?.version ?? '').trim();
  const exportDownloadUrl = String(exportData?.download_url ?? '').trim();
  const exportFileName = String(exportData?.file_name ?? '').trim();
  const exportValidationPassed = exportData?.validation_passed === true;
  const exportValidationErrors = Array.isArray(exportData?.validation_errors) ? exportData?.validation_errors : [];
  const exportColumnCount = Array.isArray(exportData?.columns) ? exportData?.columns.length : 0;
  const exportRowCount = Array.isArray(exportData?.rows) ? exportData?.rows.length : 0;
  const exportTemplatePlatform = String(exportData?.template?.platform ?? adapterPlatform ?? '').trim();
  const exportTemplateCategory = String(exportData?.template?.category ?? '').trim();
  const exportTemplateKey = String(exportData?.template?.template_key ?? exportData?.template?.key ?? '').trim();

  const keywordBadgeMap = useMemo(
    () => ({
      conversion: {
        label: t('studio.keyword.conversion'),
        badgeClass: 'border-blue-200 bg-blue-50 text-blue-600',
      },
      transactional: {
        label: t('studio.keyword.transactional'),
        badgeClass: 'border-amber-200 bg-amber-50 text-amber-600',
      },
      information: {
        label: t('studio.keyword.information'),
        badgeClass: 'border-purple-200 bg-purple-50 text-purple-600',
      },
      informational: {
        label: t('studio.keyword.information'),
        badgeClass: 'border-purple-200 bg-purple-50 text-purple-600',
      },
      default: {
        label: t('studio.keyword.other'),
        badgeClass: 'border-slate-200 bg-slate-50 text-slate-600',
      }
    }),
    [t]
  );

  const volumeTierMap = useMemo(
    () => ({
      high: {
        label: t('studio.seo.volumeHigh'),
        badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-600',
        size: 16,
      },
      medium: {
        label: t('studio.seo.volumeMedium'),
        badgeClass: 'border-blue-200 bg-blue-50 text-blue-600',
        size: 12,
      },
      low: {
        label: t('studio.seo.volumeLow'),
        badgeClass: 'border-slate-200 bg-slate-50 text-slate-600',
        size: 10,
      },
      default: {
        label: t('studio.seo.volumeUnknown'),
        badgeClass: 'border-slate-200 bg-slate-50 text-slate-600',
        size: 10,
      }
    }),
    [t]
  );

  const keywordGraphData = useMemo(() => {
    const graph = (seoKeywordGraph as { keyword_graph?: { nodes?: Array<Record<string, any>>; edges?: Array<Record<string, any>> } })?.keyword_graph;
    const nodes = Array.isArray(graph?.nodes) ? graph?.nodes : [];
    const edges = Array.isArray(graph?.edges) ? graph?.edges : [];
    return { nodes, edges };
  }, [seoKeywordGraph]);

  const keywordGraphLayout = useMemo(() => {
    const width = 520;
    const height = 240;
    const nodes = keywordGraphData.nodes
      .map((node) => ({
        keyword: String(node?.kw ?? node?.keyword ?? '').trim(),
        intent: String(node?.intent ?? '').trim(),
        score: node?.score !== undefined ? Number(node.score) : 0,
        cluster: String(node?.cluster ?? '').trim(),
        volumeTier: String(node?.volume_tier ?? node?.volumeTier ?? '').trim()
      }))
      .filter((item) => item.keyword);

    if (!nodes.length) {
      return { width, height, nodes: [], edges: [] as Array<{ from: string; to: string; type: string; start: { x: number; y: number }; end: { x: number; y: number } }> };
    }

    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 32;
    const positions = new Map<string, { x: number; y: number }>();
    const layoutNodes = nodes.map((node, index) => {
      const angle = (2 * Math.PI * index) / nodes.length - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      positions.set(node.keyword, { x, y });
      return { ...node, x, y };
    });

    const layoutEdges = keywordGraphData.edges
      .map((edge) => {
        const from = String(edge?.from ?? edge?.from_kw ?? edge?.fromKw ?? '').trim();
        const to = String(edge?.to ?? edge?.to_kw ?? edge?.toKw ?? '').trim();
        if (!from || !to) {
          return null;
        }
        const start = positions.get(from);
        const end = positions.get(to);
        if (!start || !end) {
          return null;
        }
        return { from, to, type: String(edge?.type ?? 'related'), start, end };
      })
      .filter((item): item is { from: string; to: string; type: string; start: { x: number; y: number }; end: { x: number; y: number } } => Boolean(item));

    return { width, height, nodes: layoutNodes, edges: layoutEdges };
  }, [keywordGraphData]);


  const agentRunMap = useMemo(() => {
    const map = new Map<string, AgentLogRun>();
    agentRuns.forEach((run) => {
      const stageKey = run.stage;
      const existing = map.get(stageKey);
      if (!existing) {
        map.set(stageKey, { ...run, stage: stageKey });
        return;
      }
      const existingTime = existing.started_at ? new Date(existing.started_at).getTime() : 0;
      const incomingTime = run.started_at ? new Date(run.started_at).getTime() : 0;
      if (incomingTime >= existingTime) {
        map.set(stageKey, { ...run, stage: stageKey });
      }
    });
    return map;
  }, [agentRuns]);

  const agentStageList = useMemo(
    () => [
      {
        key: 'visual',
        label: t('studio.agent.visual'),
        status: workflowState.visual,
        icon: <Palette className="text-amber-500" style={{ fontSize: 18 }} />,
      },
      {
        key: 'seo',
        label: t('studio.agent.seo'),
        status: workflowState.seo,
        icon: <ManageSearch className="text-emerald-500" style={{ fontSize: 18 }} />,
      },
      {
        key: 'listing',
        label: t('studio.agent.listing'),
        status: workflowState.listing,
        icon: <EditNote className="text-slate-600" style={{ fontSize: 18 }} />,
      },
      {
        key: 'adapter',
        label: t('studio.agent.adapter'),
        status: workflowState.adapter,
        icon: <Hub className="text-pink-500" style={{ fontSize: 18 }} />,
      },
      {
        key: 'export',
        label: t('studio.agent.export'),
        status: workflowState.export,
        icon: <CloudDownload className="text-emerald-600" style={{ fontSize: 18 }} />,
      },
      {
        key: 'info_structure',
        label: t('studio.agent.productUnderstanding'),
        status: workflowState.source,
        icon: <Dns className="text-blue-500" style={{ fontSize: 18 }} />,
      },
    ],
    [t, workflowState]
  );

  const agentEventLabelMap = useMemo(
    () => ({
      start: t('studio.agentLogs.event.start'),
      thinking: t('studio.agentLogs.event.thinking'),
      result: t('studio.agentLogs.event.result'),
      error: t('studio.agentLogs.event.error'),
      cache_hit: t('studio.agentLogs.event.cacheHit'),
      skip: t('studio.agentLogs.event.skip'),
    }),
    [t]
  );

  const resolveEventColor = useCallback((eventType: string) => {
    switch (eventType) {
      case 'error':
        return 'text-red-400';
      case 'result':
        return 'text-blue-400';
      case 'cache_hit':
        return 'text-amber-400';
      case 'skip':
        return 'text-slate-400';
      case 'start':
        return 'text-sky-400';
      default:
        return 'text-emerald-400';
    }
  }, []);

  const resolveEventDot = useCallback((eventType: string) => {
    switch (eventType) {
      case 'error':
        return 'bg-red-500';
      case 'result':
        return 'bg-blue-500';
      case 'cache_hit':
        return 'bg-amber-400';
      case 'skip':
        return 'bg-slate-400';
      case 'start':
        return 'bg-sky-400';
      default:
        return 'bg-emerald-400';
    }
  }, []);

  const resolveRunStatusMeta = useCallback(
    (status?: string | null) => {
      switch (status) {
        case 'running':
          return { label: t('studio.agentLogs.status.running'), className: 'text-emerald-500', badge: 'bg-emerald-50 border-emerald-200' };
        case 'failed':
          return { label: t('studio.agentLogs.status.failed'), className: 'text-red-500', badge: 'bg-red-50 border-red-200' };
        case 'completed':
          return { label: t('studio.agentLogs.status.completed'), className: 'text-blue-500', badge: 'bg-blue-50 border-blue-200' };
        default:
          return { label: t('studio.agentLogs.status.idle'), className: 'text-slate-400', badge: 'bg-slate-50 border-slate-200' };
      }
    },
    [t]
  );

  const resolveAgentStatusMeta = useCallback(
    (status: AgentStatus) => {
      switch (status) {
        case 'COMPLETE':
          return { label: t('studio.status.complete'), className: 'text-emerald-600', badge: 'bg-emerald-50 border-emerald-200' };
        case 'ACTIVE':
          return { label: t('studio.status.processing'), className: 'text-blue-600', badge: 'bg-blue-50 border-blue-200' };
        case 'THINKING':
          return { label: t('studio.status.thinking'), className: 'text-amber-600', badge: 'bg-amber-50 border-amber-200' };
        case 'ERROR':
          return { label: t('studio.status.error'), className: 'text-red-600', badge: 'bg-red-50 border-red-200' };
        default:
          return { label: t('studio.status.waiting'), className: 'text-slate-500', badge: 'bg-slate-50 border-slate-200' };
      }
    },
    [t]
  );

  const fetchPreview = useCallback(async () => {
    if (!collectionId) {
      return;
    }
    const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/preview`, {
      headers: authHeaders
    });
    if (!response.ok) {
      throw new Error(t('studio.errors.previewRequestFailed', { status: response.status }));
    }
    const data = (await response.json()) as CollectionPreview;
    setPreview(data);
  }, [apiBaseUrl, authHeaders, collectionId, t]);

  const fetchImagePlans = useCallback(async () => {
    if (!collectionId) {
      return;
    }
    const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/image-plans`, {
      headers: authHeaders
    });
    if (!response.ok) {
      throw new Error(t('studio.errors.imagePlanRequestFailed', { status: response.status }));
    }
    const data = (await response.json()) as { image_plans?: ImagePlan[] | null };
    const plans = normalizeImagePlans(data);
    setImagePlans(plans);
    setPromptDrafts((prev) => {
      const next = { ...prev };
      for (const plan of plans) {
        const index = plan.index ?? 0;
        if (index && !next[index]) {
          next[index] = plan.prompt ?? '';
        }
      }
      return next;
    });
  }, [apiBaseUrl, authHeaders, collectionId, t]);

  const fetchStageStatus = useCallback(async () => {
    if (!collectionId) {
      return;
    }
    const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/stage-status`, {
      headers: authHeaders
    });
    if (!response.ok) {
      throw new Error(t('studio.errors.stageStatusRequestFailed', { status: response.status }));
    }
    const data = (await response.json()) as StageStatusResponse;
    setStageStatus(toStageStatusMap(data));
    setCompletedStages(Array.isArray(data.completed) ? data.completed : []);
    setFailedStage(data.failed ?? null);
    if (data.stages?.visual) {
      await fetchImagePlans();
    }
  }, [apiBaseUrl, authHeaders, collectionId, fetchImagePlans, t]);

  const fetchAgentLogs = useCallback(async () => {
    if (!collectionId) {
      return;
    }
    const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/agent-logs`, {
      headers: authHeaders
    });
    if (!response.ok) {
      throw new Error(t('studio.errors.agentLogRequestFailed', { status: response.status }));
    }
    const data = (await response.json()) as { runs?: AgentLogRun[] | null };
    setAgentRuns(Array.isArray(data.runs) ? data.runs : []);
  }, [apiBaseUrl, authHeaders, collectionId, t]);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());
  const [expandedTracks, setExpandedTracks] = useState<Set<string>>(new Set());
  const [productUnderstandingExpanded, setProductUnderstandingExpanded] = useState(false);

  const togglePrompt = (index: number) => {
    const newSet = new Set(expandedPrompts);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setExpandedPrompts(newSet);
  };

  const toggleTrack = (trackId: string) => {
    const newSet = new Set(expandedTracks);
    if (newSet.has(trackId)) {
      newSet.delete(trackId);
    } else {
      newSet.add(trackId);
    }
    setExpandedTracks(newSet);
  };
  const toggleRichWidget = useCallback((key: string) => {
    setExpandedRichWidgetKey((prev) => (prev === key ? null : key));
  }, []);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (!collectionId) {
      setLoadError(t('studio.errors.missingCollection'));
      return;
    }

    let isActive = true;
    const run = async () => {
      try {
        await fetchPreview();
        if (isActive) {
          setLoadError('');
        }
      } catch (error) {
        if (isActive) {
          setLoadError(error instanceof Error ? error.message : t('studio.errors.previewFailed'));
        }
      }
    };

    run();
    return () => {
      isActive = false;
    };
  }, [collectionId, fetchPreview, isDemo]);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (!collectionId) {
      return;
    }

    let isActive = true;
    const run = async () => {
      try {
        await fetchStageStatus();
        await fetchAgentLogs();
        if (isActive) {
          setLoadError('');
          setAgentLogError('');
        }
      } catch (error) {
        if (isActive) {
          setLoadError(error instanceof Error ? error.message : t('studio.errors.stageStatusFailed'));
          setAgentLogError(error instanceof Error ? error.message : t('studio.errors.agentLogFailed'));
        }
      }
    };

    run();
    const timer = window.setInterval(run, 2000);
    return () => {
      isActive = false;
      window.clearInterval(timer);
    };
  }, [collectionId, fetchAgentLogs, fetchStageStatus, isDemo]);

  const toggleAgentStage = useCallback((stageKey: string) => {
    agentAutoExpandRef.current = true;
    setExpandedAgentStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageKey)) {
        next.delete(stageKey);
      } else {
        next.add(stageKey);
      }
      return next;
    });
  }, []);

  const toggleLogEntry = useCallback((entryKey: string) => {
    setExpandedLogEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryKey)) {
        next.delete(entryKey);
      } else {
        next.add(entryKey);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (expandedAgentStages.size || agentAutoExpandRef.current) {
      return;
    }
    const runningAgent = agentStageList.find((item) => agentRunMap.get(item.key)?.status === 'running' || item.status === 'ACTIVE');
    const withEvents = agentStageList.find((item) => (agentRunMap.get(item.key)?.events.length ?? 0) > 0);
    const candidate = runningAgent ?? withEvents;
    if (candidate) {
      setExpandedAgentStages(new Set([candidate.key]));
      agentAutoExpandRef.current = true;
    }
  }, [agentRunMap, agentStageList, expandedAgentStages.size]);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    expandedAgentStages.forEach((stageKey) => {
      const node = agentLogRefs.current[stageKey];
      if (node) {
        node.scrollTop = node.scrollHeight;
      }
    });
  }, [agentRuns, expandedAgentStages, isDemo]);

  useEffect(() => {
    if (isDemo) {
      return;
    }
    if (!visualPlans.length || expandedPrompts.size) {
      return;
    }
    const firstIndex = visualPlans[0]?.index ?? 1;
    setExpandedPrompts(new Set([firstIndex]));
  }, [expandedPrompts.size, visualPlans]);


  const handleGenerate = useCallback(async () => {
    if (isDemo) {
      return;
    }
    if (!collectionId || isGenerating) {
      return;
    }
    setIsGenerating(true);
    setActionError('');
    setActionMessage('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/generate-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(buildGeneratePayload({ marketStatus, skipImages }))
      });
      if (!response.ok) {
        throw new Error(t('studio.errors.generateRequestFailed', { status: response.status }));
      }
      setActionMessage(t('studio.action.generateStarted'));
      await fetchStageStatus();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : t('studio.errors.generateFailed'));
    } finally {
      setIsGenerating(false);
    }
  }, [apiBaseUrl, authHeaders, collectionId, fetchStageStatus, isDemo, isGenerating, marketStatus, skipImages, t]);

  const handleExecuteStage = useCallback(async () => {
    if (isDemo) {
      return;
    }
    if (!collectionId || !selectedStage || isExecutingStage) {
      return;
    }
    setIsExecutingStage(true);
    setActionError('');
    setActionMessage('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/execute-stage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ stage: selectedStage, use_cache: false })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const detail = payload?.detail;
        const message = typeof detail === 'string' ? detail : detail?.message;
        throw new Error(message || t('studio.errors.stageExecuteRequestFailed', { status: response.status }));
      }
      if (payload?.success === false) {
        setActionError(payload?.error || t('studio.errors.stageExecuteFailed'));
        await fetchStageStatus();
        return;
      }
      setActionMessage(t('studio.action.stageTriggered', { stage: selectedStageLabel }));
      await fetchStageStatus();
      await fetchPreview();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : t('studio.errors.stageExecuteFailed'));
    } finally {
      setIsExecutingStage(false);
    }
  }, [apiBaseUrl, authHeaders, collectionId, fetchPreview, fetchStageStatus, isDemo, isExecutingStage, selectedStageLabel, t]);

  const handlePromptChange = useCallback((planIndex: number, value: string) => {
    setPromptDrafts((prev) => ({ ...prev, [planIndex]: value }));
  }, []);

  const handleSavePrompt = useCallback(
    async (planIndex: number) => {
      if (!collectionId) {
        return;
      }
      const prompt = promptDrafts[planIndex] ?? '';
      setActionError('');
      setActionMessage('');
      try {
        const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/image-plans/${planIndex}/prompt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify({ prompt })
        });
        if (!response.ok) {
          throw new Error(t('studio.errors.promptUpdateFailed'));
        }
        setActionMessage(t('studio.action.promptUpdated', { index: planIndex }));
        await fetchImagePlans();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : t('studio.errors.promptUpdateFailed'));
      }
    },
    [apiBaseUrl, authHeaders, collectionId, fetchImagePlans, promptDrafts, t]
  );

  const handleGenerateImage = useCallback(
    async (planIndex: number) => {
      if (!collectionId) {
        return;
      }
      setActionError('');
      setActionMessage('');
      try {
        const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/generate-images`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify({ plan_indices: [planIndex] })
        });
        if (!response.ok) {
          throw new Error(t('studio.errors.imageGenerateFailed'));
        }
        setActionMessage(t('studio.action.imageGenerateTriggered', { index: planIndex }));
        await fetchImagePlans();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : t('studio.errors.imageGenerateFailed'));
      }
    },
    [apiBaseUrl, authHeaders, collectionId, fetchImagePlans, t]
  );

  const handleRegenerateImage = useCallback(
    async (planIndex: number) => {
      if (!collectionId) {
        return;
      }
      const prompt = promptDrafts[planIndex];
      setActionError('');
      setActionMessage('');
      try {
        const response = await fetch(`${apiBaseUrl}/api/v1/collections/${collectionId}/regenerate-image/${planIndex}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders
          },
          body: JSON.stringify({ custom_prompt: prompt || null })
        });
        if (!response.ok) {
          throw new Error(t('studio.errors.imageRegenerateFailed'));
        }
        setActionMessage(t('studio.action.imageRegenerateTriggered', { index: planIndex }));
        await fetchImagePlans();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : t('studio.errors.imageRegenerateFailed'));
      }
    },
    [apiBaseUrl, authHeaders, collectionId, fetchImagePlans, promptDrafts, t]
  );

  const renderPlanRow = (plan: ImagePlan, fallbackIndex: number) => {
    const planIndex = plan.index ?? fallbackIndex + 1;
    const planScene = (plan as { scene?: string }).scene ?? plan.image_type ?? '';
    const imageTypeLabel = plan.image_type
      ? plan.image_type.toUpperCase()
      : planScene
        ? String(planScene).toUpperCase()
        : t('studio.visual.planDefaultType');
    const badgeLabel = t('studio.visual.planBadge', { index: String(planIndex).padStart(2, '0'), type: imageTypeLabel });
    const title = plan.image_type
      ? t('studio.visual.planTypeTitle', { type: imageTypeLabel })
      : t('studio.visual.planTitle', { index: planIndex });
    const subtitle = plan.aspect_ratio
      ? t('studio.visual.planAspect', { ratio: plan.aspect_ratio })
      : t('studio.visual.planSubtitle');
    const canEditPlan = Boolean(visualPlans.length);
    const status = plan.generated_url
      ? 'complete'
      : workflowState.visual === 'ACTIVE'
        ? 'processing'
        : 'waiting';
    const promptValue = promptDrafts[planIndex] ?? plan.prompt ?? '';

    return (
      <VisualPlanRow
        key={`${planIndex}-${plan.image_type ?? 'plan'}`}
        badge={badgeLabel}
        title={title}
        subtitle={subtitle}
        prompt={plan.prompt ?? ''}
        keywords={(plan as { keywords?: string[] }).keywords}
        scene={planScene ? String(planScene) : undefined}
        status={status}
        isExpanded={expandedPrompts.has(planIndex)}
        onToggle={() => togglePrompt(planIndex)}
        editablePrompt={canEditPlan ? promptValue : undefined}
        onPromptChange={canEditPlan ? (value) => handlePromptChange(planIndex, value) : undefined}
        onSavePrompt={canEditPlan ? () => handleSavePrompt(planIndex) : undefined}
        onGenerate={canEditPlan ? () => handleGenerateImage(planIndex) : undefined}
        onRegenerate={canEditPlan && plan.generated_url ? () => handleRegenerateImage(planIndex) : undefined}
        generatedUrl={plan.generated_url}
      />
    );
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans">
      <style>{`
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
        @keyframes sheen {
          0% { transform: translateX(-150%) skewX(12deg); }
          40%, 100% { transform: translateX(200%) skewX(12deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes jitter {
          0% { transform: translate(0, 0); }
          25% { transform: translate(-2px, 2px); }
          50% { transform: translate(2px, -2px); }
          75% { transform: translate(-2px, -2px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes logTyping {
          0% { max-width: 0; opacity: 0; }
          100% { max-width: 100%; opacity: 1; }
        }
        @keyframes logBlink {
          0%, 100% { border-color: rgba(148, 163, 184, 0.4); }
          50% { border-color: transparent; }
        }
        .agent-log-typing {
          display: inline-block;
          white-space: nowrap;
          overflow: hidden;
          max-width: 0;
          border-right: 1px solid rgba(148, 163, 184, 0.4);
          animation: logTyping 0.9s steps(24, end) forwards, logBlink 0.9s step-end infinite;
        }
      `}</style>

      {/* Header */}
      <Header
        maxWidth="w-full"
        breadcrumbs={[
          { label: t('profile.workspace'), path: '/workspace' },
          { label: t('ai.workspaceTitle') }
        ]}
        rightElement={
          <div className="flex flex-col items-end w-48">
            <div className="flex items-center justify-between w-full mb-1">
              <span className="text-[10px] font-bold text-primary uppercase tracking-wider flex items-center gap-1">
                {workflowState.progress < 100 ? <Sync className="animate-spin text-[10px]" /> : <CheckCircle className="text-[10px]" />}
                {t('ai.syncProgress')}
              </span>
              <span className="text-[10px] font-black text-slate-700">{workflowState.progress}%</span>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                style={{ width: `${workflowState.progress}%` }}
              >
                <div className="absolute inset-0 bg-white/30 w-full animate-[shimmer_2s_infinite]"></div>
              </div>
            </div>
          </div>
        }
      />

      <main className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <aside className={`${isSidebarOpen ? 'w-64' : 'w-16'} border-r border-slate-200 bg-white hidden lg:flex flex-col transition-all duration-300 ease-in-out relative z-40 shadow-[4px_0_24px_rgba(0,0,0,0.02)]`}>
          <div className="flex-1 overflow-y-auto py-6 flex flex-col gap-2 w-full px-2">
            <SidebarButton icon={<LinkIcon className="text-orange-500" />} label={t('ai.sourceRef')} isOpen={isSidebarOpen} active />
            <SidebarButton icon={<ImageIcon className="text-blue-500" />} label={t('ai.rawMedia')} isOpen={isSidebarOpen} />
            <SidebarButton icon={<VerifiedUser className="text-emerald-500" />} label={t('ai.brandProt')} isOpen={isSidebarOpen} />
          </div>
          <div className="shrink-0 p-4 border-t border-slate-100 flex justify-center bg-slate-50/50">
            <button
              onClick={toggleSidebar}
              className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 hover:border-primary hover:bg-blue-50 transition-all text-slate-400 hover:text-primary bg-white shadow-sm"
            >
              {isSidebarOpen ? <ChevronLeft className="text-lg" /> : <ChevronRight className="text-lg" />}
            </button>
          </div>
        </aside>

        {/* Main Canvas - Single Column, Compact */}
        <section className="flex-1 bg-[#F8FAFC] overflow-y-auto p-4 lg:p-8 scroll-smooth relative">
          <div className="max-w-6xl mx-auto flex flex-col gap-4 pb-20 transition-all duration-300">
            {(loadError || failedStage) && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[11px] font-semibold text-red-600">
                {loadError || t('studio.errors.stageFailed', { stage: failedStage ?? '' })}
              </div>
            )}
            {actionError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[11px] font-semibold text-red-600">
                {actionError}
              </div>
            )}
            {actionMessage && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-[11px] font-semibold text-emerald-700">
                {actionMessage}
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-xs font-bold text-slate-800">{t('studio.control.title')}</h3>
                  <p className="text-[10px] text-slate-500">
                    {t('studio.control.collection')} {resolvedCollectionId || t('studio.control.collectionEmpty')}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span>{t('studio.control.marketStatus')}</span>
                    <select
                      value={marketStatus}
                      onChange={(event) => setMarketStatus(event.target.value)}
                      className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px]"
                    >
                      {MARKET_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] text-slate-600">
                    <input
                      type="checkbox"
                      checked={skipImages}
                      onChange={(event) => setSkipImages(event.target.checked)}
                      className="h-3 w-3 rounded border-slate-300"
                    />
                    {t('studio.control.skipImages')}
                  </label>
                  <button
                    onClick={handleGenerate}
                    disabled={!resolvedCollectionId || isGenerating}
                    className={`rounded-lg px-4 py-2 text-[11px] font-semibold text-white transition-all ${
                      isGenerating || !resolvedCollectionId ? 'bg-slate-300 cursor-not-allowed' : 'bg-primary hover:bg-blue-700'
                    }`}
                  >
                    {isGenerating ? t('studio.control.starting') : t('studio.control.start')}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] text-slate-600">
                <span className="font-semibold text-slate-700">{t('studio.control.stageDebug')}</span>
                <select
                  value={selectedStage}
                  onChange={(event) => setSelectedStage(event.target.value)}
                  className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px]"
                >
                  {STAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleExecuteStage}
                  disabled={!resolvedCollectionId || isExecutingStage}
                  className={`rounded-lg px-4 py-2 text-[11px] font-semibold text-white transition-all ${
                    isExecutingStage || !resolvedCollectionId ? 'bg-slate-300 cursor-not-allowed' : 'bg-slate-800 hover:bg-slate-900'
                  }`}
                >
                  {isExecutingStage ? t('studio.control.executing') : t('studio.control.runStage')}
                </button>
              </div>
            </div>

            {/* Module 6: Product Understanding */}
            <AgentModule
              status={workflowState.source}
              themeColor="blue"
              className="order-6 p-0 overflow-hidden"
              header={
                <div className="flex items-center justify-between w-full">
                  <h2 className="font-bold flex items-center gap-2 text-slate-900 text-sm">
                    <Biotech className={`text-lg transition-colors ${workflowState.source === 'ACTIVE' ? 'text-blue-600 animate-pulse' : 'text-blue-500'}`} />
                    {t('studio.source.title')}
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setProductUnderstandingExpanded((prev) => !prev)}
                      className="text-[10px] font-semibold text-blue-600 hover:text-blue-700"
                    >
                      {productUnderstandingExpanded ? t('studio.source.collapse') : t('studio.source.expand')}
                    </button>
                    <div
                      className={`flex items-center gap-1.5 px-2 py-0.5 rounded border ${
                        hasExtractedData ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <CheckCircle style={{ fontSize: 14 }} className={hasExtractedData ? 'text-emerald-500' : 'text-slate-300'} />
                      <span className={`text-[10px] font-bold uppercase tracking-tight ${hasExtractedData ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {hasExtractedData ? t('studio.source.verified') : t('studio.source.pending')}
                      </span>
                    </div>
                  </div>
                </div>
              }
            >
              <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4 space-y-4">
                  <div className="relative rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="aspect-square w-full flex items-center justify-center bg-slate-50">
                      {mainPreviewImage ? (
                        <img alt={t('studio.source.mainImageAlt')} className="w-full h-full object-cover" src={mainPreviewImage} />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                          <ImageIcon className="text-slate-300" style={{ fontSize: 32 }} />
                          <span className="mt-2 text-[10px] font-semibold text-slate-500">{t('studio.source.emptyImages')}</span>
                        </div>
                      )}
                    </div>
                    <ScanningOverlay active={workflowState.source === 'ACTIVE'} />
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {thumbnailSlots.map((src, index) => (
                      <div
                        key={`${src ?? 'thumb'}-${index}`}
                        className={`w-12 h-12 rounded-lg border overflow-hidden flex items-center justify-center shrink-0 ${
                          index === 0 ? 'border-blue-500' : 'border-slate-200'
                        }`}
                      >
                        {src ? (
                          <img alt={t('studio.source.thumbnailAlt')} className="w-full h-full object-cover" src={src} />
                        ) : (
                          <ImageIcon className="text-slate-300" style={{ fontSize: 16 }} />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-500 flex items-center justify-between">
                    <span>
                      {hasPreviewImages ? t('studio.source.imageCount', { count: previewImages.length }) : t('studio.source.noImages')}
                    </span>
                    <span>{hasRawText ? t('studio.rawText.length', { count: rawPageText.length }) : t('studio.rawText.emptyTitle')}</span>
                  </div>
                </div>
                <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.productSchema.identity')}</div>
                    <div className="mt-3 space-y-2 text-[11px] text-slate-600">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] font-bold uppercase text-slate-400">{t('studio.productSchema.titleLabel')}</span>
                        <span className="font-semibold text-slate-800 text-right">{previewTitle}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] font-bold uppercase text-slate-400">{t('studio.productSchema.brandLabel')}</span>
                        <span className="font-semibold text-slate-800 text-right">{previewBrand}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[9px] font-bold uppercase text-slate-400">{t('studio.productSchema.categoryLabel')}</span>
                        <span className="font-semibold text-slate-800 text-right">{previewCategory}</span>
                      </div>
                      {productType && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[9px] font-bold uppercase text-slate-400">{t('studio.productCore.title')}</span>
                          <span className="font-semibold text-slate-800 text-right">{productType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.productSchema.sku')}</div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                        <span className="text-slate-400 font-semibold">{t('studio.productSchema.skuCount')}</span>
                        <span className="font-bold text-slate-800">{skuCount}</span>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                        <span className="text-slate-400 font-semibold">{t('studio.productSchema.price')}</span>
                        <span className="font-bold text-slate-800">{priceLabel}</span>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                        <span className="text-slate-400 font-semibold">{t('studio.productSchema.weight')}</span>
                        <span className="font-bold text-slate-800">{skuInsights.weightText ?? t('studio.productSchema.emptyValue')}</span>
                      </div>
                      <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                        <span className="text-slate-400 font-semibold">{t('studio.productSchema.dimensions')}</span>
                        <span className="font-bold text-slate-800">{skuInsights.dimensionsText ?? t('studio.productSchema.emptyValue')}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[9px]">
                      {skuInsights.colors.slice(0, 4).map((color) => (
                        <span key={color} className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 font-semibold">
                          {color}
                        </span>
                      ))}
                      {skuInsights.colors.length === 0 && (
                        <span className="text-[9px] text-slate-400">{t('studio.productSchema.noColors')}</span>
                      )}
                      {skuInsights.sizes.slice(0, 3).map((size) => (
                        <span key={size} className="px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-600 font-semibold">
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 md:col-span-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.productSchema.attributes')}</span>
                      <span className="text-[9px] text-slate-400">{highlightDisplay.length ? highlightDisplay.length : 0}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[9px]">
                      {highlightDisplay.length ? (
                        highlightDisplay.slice(0, 8).map((item) => (
                          <span
                            key={`${item.title}-${item.detail}`}
                            className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-semibold"
                          >
                            {item.title}: {item.detail}
                          </span>
                        ))
                      ) : (
                        <span className="text-[9px] text-slate-400">{t('studio.productSchema.empty')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {productUnderstandingExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/70 p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.rawText.title')}</div>
                    <div className="mt-2 text-[11px] text-slate-600 leading-relaxed max-h-40 overflow-auto whitespace-pre-wrap">
                      {hasRawText ? rawPageText : <span className="text-[10px] text-slate-400">{t('studio.rawText.emptyDesc')}</span>}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.imageText.title')}</div>
                    <div className="mt-2 text-[11px] text-slate-600 leading-relaxed max-h-32 overflow-auto whitespace-pre-wrap">
                      {hasImageExtractedText ? (
                        imageExtractedText
                      ) : (
                        <span className="text-[10px] text-slate-400">{t('studio.imageText.emptyDesc')}</span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.source.sectionsTitle')}</div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {rawSectionEntries.length ? (
                        rawSectionEntries.map((section) => (
                          <div key={section.key} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{section.key}</div>
                            <div className="mt-1 text-[10px] text-slate-600 line-clamp-3">{section.preview}</div>
                          </div>
                        ))
                      ) : (
                        <span className="text-[9px] text-slate-400">{t('studio.source.sectionsEmpty')}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </AgentModule>

            {/* Module 2: SEO Keyword Graph */}
            <AgentModule
              status={workflowState.seo}
              themeColor="emerald"
              className={`${workflowState.seo === 'COMPLETE' ? 'ring-2 ring-emerald-500/50' : ''} order-2`.trim()}
              header={
                <div className="flex items-center justify-between w-full">
                  <h2 className="font-bold flex items-center gap-2 text-slate-900 text-base">
                    <ManageSearch className={`text-xl transition-colors ${workflowState.seo === 'THINKING' || workflowState.seo === 'ACTIVE' ? 'text-emerald-600 animate-pulse' : 'text-emerald-500'}`} />
                    {t('ai.seoArch')}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.seo.strength')}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500 rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${seoScoreTarget}%` }}
                          ></div>
                        </div>
                        <span className="text-[11px] font-bold text-emerald-500">{seoScoreTarget}/100</span>
                      </div>
                    </div>
                  </div>
                </div>
              }
            >
              <div className="p-8 grid grid-cols-1 gap-8">
                {workflowState.seo === 'ERROR' ? (
                  <div className="flex flex-col gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-red-500 font-bold flex items-center gap-2">
                      <StopCircle fontSize="small" /> {t('studio.seo.failed')}
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      {stageStatus.seo?.error || t('studio.seo.failedDesc')}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.seo.optimizedKeywords')}</label>
                        <div className="flex gap-2 opacity-50">
                          <span className="text-[9px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200">{t('studio.keyword.conversion')}</span>
                          <span className="text-[9px] px-2 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">{t('studio.keyword.transactional')}</span>
                          <span className="text-[9px] px-2 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-200">{t('studio.keyword.information')}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-auto pr-1">
                        {keywordCards.length === 0 ? (
                          <div className="col-span-full rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 p-6 flex flex-col md:flex-row gap-4 items-start">
                            <div className="w-16 h-16 rounded-2xl bg-white border border-emerald-100 flex items-center justify-center shadow-sm">
                              <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="16" stroke="#34D399" strokeWidth="2" />
                                <path d="M24 14v10l6 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="text-[12px] font-semibold text-emerald-700">{t('studio.seo.emptyTitle')}</div>
                              <div className="text-[10px] text-emerald-600/80 mt-1">{t('studio.seo.emptyDesc')}</div>
                              <div className="mt-3 flex flex-wrap gap-2 text-[9px] font-semibold">
                                <span className="px-2 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-100">{t('studio.keyword.conversion')}</span>
                                <span className="px-2 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-100">{t('studio.keyword.transactional')}</span>
                                <span className="px-2 py-0.5 rounded-full bg-white text-emerald-700 border border-emerald-100">{t('studio.keyword.information')}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          keywordCards.map((item) => {
                            const badge = keywordBadgeMap[item.bucket as keyof typeof keywordBadgeMap] ?? keywordBadgeMap.default;
                            const volumeTier = item.volumeTier?.toLowerCase() || 'default';
                            const volumeBadge = volumeTierMap[volumeTier as keyof typeof volumeTierMap] ?? volumeTierMap.default;
                            return (
                              <div
                                key={`${item.keyword}-${item.bucket}`}
                                className={`flex flex-col justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-400 transition-all shadow-sm group ${
                                  workflowState.seo !== 'COMPLETE' && workflowState.seo !== 'ACTIVE' ? 'opacity-50 grayscale' : ''
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-[12px] font-bold text-slate-900">{item.keyword}</span>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-tight ${badge.badgeClass}`}>
                                    {badge.label}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {item.cluster && (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                                      {t('studio.seo.clusterLabel')}: {item.cluster}
                                    </span>
                                  )}
                                  <span className={`text-[9px] px-2 py-0.5 rounded-full border ${volumeBadge.badgeClass}`}>
                                    {t('studio.seo.volumeTier')}: {volumeBadge.label}
                                  </span>
                                </div>
                                <div className="flex items-end justify-between gap-2">
                                  <div className="flex flex-col">
                                    <span className="text-[9px] text-slate-400 uppercase font-medium">{t('studio.seo.searchVolume')}</span>
                                    <span className="text-[11px] font-bold text-slate-700">
                                      {item.searchVolume || t('studio.seo.unknown')}
                                    </span>
                                  </div>
                                  <div className="flex flex-col text-right">
                                    <span className="text-[9px] text-slate-400 uppercase font-medium">{t('studio.seo.competition')}</span>
                                    <span className="text-[11px] font-bold text-slate-700">
                                      {item.competition || t('studio.seo.unknown')}
                                    </span>
                                  </div>
                                </div>
                                {item.meaning && (
                                  <div className="mt-2 text-[10px] text-slate-500">{item.meaning}</div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.seo.graphTitle')}</label>
                      <div className="rounded-lg border border-slate-200 bg-white p-4">
                        {keywordGraphLayout.nodes.length === 0 ? (
                          <span className="text-[10px] text-slate-400">{t('studio.seo.graphEmpty')}</span>
                        ) : (
                          <div className="w-full overflow-x-auto">
                            <svg
                              viewBox={`0 0 ${keywordGraphLayout.width} ${keywordGraphLayout.height}`}
                              className="w-full h-64"
                            >
                              {keywordGraphLayout.edges.map((edge) => (
                                <line
                                  key={`${edge.from}-${edge.to}`}
                                  x1={edge.start.x}
                                  y1={edge.start.y}
                                  x2={edge.end.x}
                                  y2={edge.end.y}
                                  stroke="#CBD5F5"
                                  strokeWidth="1.2"
                                  strokeDasharray={edge.type === 'related' ? '4 2' : '0'}
                                />
                              ))}
                              {keywordGraphLayout.nodes.map((node) => {
                                const badge = keywordBadgeMap[node.intent as keyof typeof keywordBadgeMap] ?? keywordBadgeMap.default;
                                const volumeTier = node.volumeTier?.toLowerCase() || 'default';
                                const volumeBadge = volumeTierMap[volumeTier as keyof typeof volumeTierMap] ?? volumeTierMap.default;
                                const radius = volumeBadge.size;
                                const label = node.keyword.length > 10 ? `${node.keyword.slice(0, 10)}…` : node.keyword;
                                return (
                                  <g key={`node-${node.keyword}`}>
                                    <circle cx={node.x} cy={node.y} r={radius} fill="#FFFFFF" stroke="#94A3B8" strokeWidth="1.5" />
                                    <circle cx={node.x} cy={node.y} r={radius - 3} fill="none" stroke="#34D399" strokeWidth="2" opacity="0.3" />
                                    <text x={node.x} y={node.y - radius - 4} textAnchor="middle" fontSize="9" fill="#475569">
                                      {label}
                                    </text>
                                    <text x={node.x} y={node.y + 3} textAnchor="middle" fontSize="8" fill="#64748B">
                                      {badge.label}
                                    </text>
                                  </g>
                                );
                              })}
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.seo.searchPipeline')}</label>
                      <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 min-h-[80px]">
                        <p className={`text-[13px] font-mono text-slate-600 leading-relaxed ${workflowState.seo === 'COMPLETE' ? '' : 'animate-pulse blur-sm'}`}>
                          {workflowState.seo === 'COMPLETE' && searchTermText ? searchTermText : t('studio.seo.pipelinePending')}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.seo.coverageTitle')}</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          { key: 'missing', title: t('studio.seo.missingAspects'), items: seoMissingAspects },
                          { key: 'duplicates', title: t('studio.seo.duplicates'), items: seoDuplicates },
                          { key: 'language', title: t('studio.seo.languageIssues'), items: seoLanguageIssues }
                        ].map((card) => (
                          <div key={card.key} className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{card.title}</div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {card.items.length ? (
                                card.items.slice(0, 6).map((item: string) => (
                                  <span
                                    key={`${card.key}-${item}`}
                                    className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[9px] font-semibold text-slate-600"
                                  >
                                    {item}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[9px] text-slate-400">{t('studio.seo.coverageEmpty')}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </AgentModule>

            {/* Module 1: Visual Strategy */}
            <AgentModule
              status={workflowState.visual}
              themeColor="blue"
              className="order-1 p-0 overflow-hidden"
              header={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Architecture className="text-blue-600 text-lg" />
                    </div>
                    <div>
                      <h2 className="font-bold text-slate-900 text-sm">{t('studio.visual.title')}</h2>
                      <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                        {t('studio.visual.subtitle', { count: visualPlans.length || 0 })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => { setExpandedPrompts(new Set()); setExpandedTracks(new Set()); }}
                      className="flex items-center gap-1.5 cursor-pointer text-blue-600 hover:opacity-80 transition-opacity text-xs font-semibold"
                    >
                      <UnfoldLess style={{ fontSize: 16 }} />
                      {t('studio.visual.collapseAll')}
                    </button>
                    <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                      <span className="text-[10px] font-bold text-blue-600 uppercase">{planStatusText}</span>
                    </div>
                  </div>
                </div>
              }
            >
              {(visualPlans.length > 0 || visualConstraints.length > 0 || visualKeywordPool.length > 0) && (
                <div className="px-8 py-4 border-b border-slate-100 bg-white">
                  <div className="flex flex-wrap items-center gap-2 text-[9px]">
                    <span className="text-slate-400 font-bold uppercase tracking-widest">{t('studio.visual.constraintsTitle')}</span>
                    {visualConstraints.length ? (
                      visualConstraints.slice(0, 6).map((item) => (
                        <span
                          key={item}
                          className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-semibold"
                        >
                          {item}
                        </span>
                      ))
                    ) : (
                      <span className="text-slate-400">{t('studio.visual.constraintsEmpty')}</span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[9px]">
                    <span className="text-slate-400 font-bold uppercase tracking-widest">{t('studio.visual.keywordsTitle')}</span>
                    {visualKeywordPool.length ? (
                      visualKeywordPool.map((item) => (
                        <span
                          key={item}
                          className="px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-600 font-semibold"
                        >
                          {item}
                        </span>
                      ))
                    ) : (
                      <span className="text-slate-400">{t('studio.visual.keywordsEmpty')}</span>
                    )}
                  </div>
                </div>
              )}
              {visualPlans.length === 0 ? (
                <div className="px-8 py-8">
                  <div className="flex flex-col lg:flex-row gap-6 items-start">
                    <div className="w-28 h-28 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center shadow-sm">
                      <AutoFixHigh className="text-blue-500" style={{ fontSize: 36 }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-slate-800">{t('studio.visual.emptyTitle')}</h4>
                      <p className="text-[11px] text-slate-500 mt-2">{t('studio.visual.emptyDesc')}</p>
                      <div className="mt-4 flex flex-wrap gap-2 text-[10px]">
                        <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 font-semibold">
                          {t('studio.visual.emptyStep1')}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 font-semibold">
                          {t('studio.visual.emptyStep2')}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                          {t('studio.visual.emptyStep3')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      {
                        title: t('studio.visual.placeholderMainTitle'),
                        desc: t('studio.visual.placeholderMainDesc'),
                        icon: <ImageIcon className="text-blue-500" style={{ fontSize: 18 }} />
                      },
                      {
                        title: t('studio.visual.placeholderSceneTitle'),
                        desc: t('studio.visual.placeholderSceneDesc'),
                        icon: <Palette className="text-amber-500" style={{ fontSize: 18 }} />
                      },
                      {
                        title: t('studio.visual.placeholderDetailTitle'),
                        desc: t('studio.visual.placeholderDetailDesc'),
                        icon: <AutoAwesome className="text-emerald-500" style={{ fontSize: 18 }} />
                      }
                    ].map((item) => (
                      <div key={item.title} className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4 flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center shadow-sm">
                          {item.icon}
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-700">{item.title}</div>
                          <div className="text-[10px] text-slate-500 mt-1">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="bg-slate-50/50 px-8 py-4 border-b border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-100/80 transition-colors group"
                    onClick={() => toggleTrack('track1')}
                  >
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] group-hover:text-slate-600 transition-colors">
                        {t('studio.visual.trackPrimary')}
                      </span>
                    <button
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold transition-all ${expandedTracks.has('track1') ? 'bg-slate-200 text-slate-600 hover:bg-slate-300' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 ring-1 ring-blue-100'}`}
                    >
                        {expandedTracks.has('track1') ? (
                          <>
                            <span>{t('studio.visual.collapse')}</span>
                            <ExpandLess style={{ fontSize: 14 }} />
                          </>
                        ) : (
                          <>
                            <span>{t('studio.visual.viewMore', { count: trackOneExtraCount })}</span>
                            <ExpandMore style={{ fontSize: 14 }} />
                          </>
                        )}
                    </button>
                  </div>

                  <div className="bg-white">
                    {trackOnePlans[0] && renderPlanRow(trackOnePlans[0], 0)}

                    {expandedTracks.has('track1') && (
                      <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                        {trackOnePlans.slice(1).map((plan, index) => renderPlanRow(plan, index + 1))}
                      </div>
                    )}
                  </div>

                  {trackTwoPlans.length > 0 && (
                    <>
                      <div
                        className="bg-slate-50/50 px-8 py-4 border-y border-slate-100 mt-0 flex items-center justify-between cursor-pointer hover:bg-slate-100/80 transition-colors group"
                        onClick={() => toggleTrack('track2')}
                      >
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] group-hover:text-slate-600 transition-colors">
                          {t('studio.visual.trackSecondary')}
                        </span>
                        <button
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold transition-all ${expandedTracks.has('track2') ? 'bg-slate-200 text-slate-600 hover:bg-slate-300' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 ring-1 ring-blue-100'}`}
                        >
                          {expandedTracks.has('track2') ? (
                            <>
                              <span>{t('studio.visual.collapse')}</span>
                              <ExpandLess style={{ fontSize: 14 }} />
                            </>
                          ) : (
                            <>
                              <span>{t('studio.visual.viewMore', { count: trackTwoExtraCount })}</span>
                              <ExpandMore style={{ fontSize: 14 }} />
                            </>
                          )}
                        </button>
                      </div>

                      <div className="bg-white">
                        {trackTwoPlans[0] && renderPlanRow(trackTwoPlans[0], trackOnePlans.length)}
                        {expandedTracks.has('track2') && (
                          <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                            {trackTwoPlans
                              .slice(1)
                              .map((plan, index) => renderPlanRow(plan, trackOnePlans.length + index + 1))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </AgentModule>

            {/* Module 3: Listing Writer */}
            <AgentModule
              status={workflowState.listing}
              themeColor="emerald"
              className="order-3"
              header={
                <>
                  <h2 className="font-semibold flex items-center gap-2 text-slate-900 text-xs">
                    <EditNote className={`text-sm transition-colors ${workflowState.listing === 'ACTIVE' ? 'text-emerald-600 animate-pulse' : 'text-emerald-500'}`} />
                    {t('studio.listing.title')}
                  </h2>
                  <StatusBadge status={workflowState.listing} color="emerald" activeLabel={t('studio.listing.writing')} />
                </>
              }
            >
              <div className="p-5">
                <div className="mb-4 flex flex-wrap items-center gap-2 text-[9px]">
                  <span className="text-slate-400 font-bold uppercase tracking-widest">{t('studio.listing.keywordInput')}</span>
                  {listingKeywordHints.length ? (
                    listingKeywordHints.map((item) => (
                      <span
                        key={item}
                        className="px-2 py-0.5 rounded-full border border-emerald-100 bg-emerald-50 text-emerald-600 font-semibold"
                      >
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400">{t('studio.listing.keywordEmpty')}</span>
                  )}
                </div>
                {!listingHasContent ? (
                  <div className="rounded-xl border border-dashed border-emerald-200 bg-emerald-50/40 p-6 flex flex-col md:flex-row gap-4 items-start">
                    <div className="w-16 h-16 rounded-2xl bg-white border border-emerald-100 flex items-center justify-center shadow-sm">
                      <EditNote className="text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[12px] font-semibold text-emerald-700">{t('studio.listing.emptyTitle')}</div>
                      <div className="text-[10px] text-emerald-600/80 mt-1">{t('studio.listing.emptyDesc')}</div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.listing.fieldTitle')}</div>
                      <div className="mt-2 text-[14px] font-semibold text-slate-900">{listingTitle || '—'}</div>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.listing.shortDesc')}</div>
                      <div className="mt-2 text-[11px] text-slate-600 leading-relaxed whitespace-pre-wrap">{listingShort || '—'}</div>
                    </div>
                    <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.listing.bullets')}</div>
                      <div className="mt-3 space-y-2">
                        {listingBullets.length ? (
                          listingBullets.slice(0, 6).map((item, index) => (
                            <div key={`${item}-${index}`} className="flex items-start gap-2 text-[11px] text-slate-700">
                              <CheckCircle className="text-emerald-500" style={{ fontSize: 14 }} />
                              <span className="leading-relaxed">{item}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-[10px] text-slate-400">{t('studio.listing.emptyBullets')}</div>
                        )}
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm lg:col-span-2">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.listing.longDesc')}</div>
                      <div className="mt-2 text-[11px] text-slate-600 leading-relaxed whitespace-pre-wrap max-h-40 overflow-auto">{listingLong || '—'}</div>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">{t('studio.listing.faq')}</div>
                      <div className="mt-2 space-y-2">
                        {listingFaq.length ? (
                          listingFaq.slice(0, 4).map((item, index) => (
                            <div key={`${item}-${index}`} className="text-[11px] text-slate-600">
                              • {item}
                            </div>
                          ))
                        ) : (
                          <div className="text-[10px] text-slate-400">{t('studio.listing.emptyFaq')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </AgentModule>

            {/* Module 4: Platform Adapter */}
            <AgentModule
              status={workflowState.adapter}
              themeColor="pink"
              className="order-4"
              header={
                <>
                  <h2 className="font-semibold flex items-center gap-2 text-slate-900 text-xs">
                    <Hub className="text-pink-500 text-sm" />
                    {t('studio.adapter.title')}
                  </h2>
                  <span className="text-[9px] font-bold bg-pink-100 text-pink-700 px-2 py-0.5 rounded border border-pink-200 uppercase">
                    {adapterPlatform || t('studio.adapter.platformUnknown')}
                  </span>
                </>
              }
            >
              <div className="flex flex-col md:flex-row h-auto">
                <div className="w-full md:w-5/12 border-r border-slate-100 p-4">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{t('studio.adapter.summary')}</label>
                  <div className="rounded-lg border border-slate-200 bg-white p-4 text-[10px] text-slate-500 space-y-2">
                    <div className="flex items-center justify-between">
                      <span>{t('studio.adapter.platform')}</span>
                      <span className="font-semibold text-slate-700">{adapterPlatform || '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{t('studio.adapter.format')}</span>
                      <span className="font-semibold text-slate-700">{adapterOutput?.format ?? '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{t('studio.adapter.fieldCount')}</span>
                      <span className="font-semibold text-slate-700">{adapterFieldCount || 0}</span>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[10px]">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.category')}</span>
                      <span className="font-semibold text-slate-700">{adapterCategoryId || previewCategory || '—'}</span>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.typeId')}</span>
                      <span className="font-semibold text-slate-700">{adapterTypeId || '—'}</span>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.price')}</span>
                      <span className="font-semibold text-slate-700">{adapterPrice || '—'}</span>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.images')}</span>
                      <span className="font-semibold text-slate-700">{adapterImageCount || 0}</span>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.attributesCount')}</span>
                      <span className="font-semibold text-slate-700">{adapterAttributeCount || 0}</span>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1">
                      <span className="text-slate-400 font-semibold">{t('studio.adapter.richContent')}</span>
                      <span className="font-semibold text-slate-700">{richContentWidgetCount || 0}</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.adapter.missingFields')}</label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {adapterMissingFields.length ? (
                        adapterMissingFields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[9px] font-semibold"
                          >
                            {field}
                          </span>
                        ))
                      ) : (
                        <span className="text-[9px] text-slate-400">{t('studio.adapter.missingFieldsEmpty')}</span>
                      )}
                    </div>
                    {adapterError && (
                      <div className="mt-2 text-[9px] text-amber-600">{adapterError}</div>
                    )}
                  </div>
                  <div className="mt-4">
                    <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.adapter.payload')}</label>
                    <pre className="mt-2 max-h-40 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-[10px] text-slate-600 whitespace-pre-wrap">
                      {adapterPayloadPreview || t('studio.adapter.emptyPayload')}
                    </pre>
                  </div>
                </div>
                <div className="w-full md:w-7/12 p-0 flex flex-col">
                  <div className="p-3 border-b border-slate-200 bg-slate-50/50">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.adapter.fieldMap')}</label>
                  </div>
                  <div className="divide-y divide-slate-100 flex-1">
                    {uploadAttributes.length === 0 ? (
                      <div className="px-4 py-6 flex items-center gap-3 text-slate-400">
                        <div className="w-10 h-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
                          <LinkIcon className="text-slate-400" style={{ fontSize: 18 }} />
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold text-slate-500">{t('studio.adapter.emptyMappingTitle')}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{t('studio.adapter.emptyMappingDesc')}</div>
                        </div>
                      </div>
                    ) : (
                      uploadAttributes.slice(0, 8).map((attr) => (
                        <div key={attr.label} className="px-4 py-2.5 flex justify-between items-center hover:bg-slate-50 transition-colors">
                          <span className="text-[10px] font-medium text-slate-500">{attr.label}</span>
                          <span className="text-[10px] font-bold text-slate-800 flex items-center gap-1">
                            {attr.value}
                            <CheckCircle style={{ fontSize: 10 }} className="text-emerald-500" />
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </AgentModule>

            {/* Module 5: Export & Delivery */}
            <AgentModule
              status={workflowState.export}
              themeColor="cyan"
              className="order-5"
              header={
                <>
                  <h2 className="font-semibold flex items-center gap-2 text-slate-900 text-xs">
                    <CloudDownload className={`text-sm transition-colors ${workflowState.export === 'ACTIVE' ? 'text-cyan-600 animate-pulse' : 'text-cyan-500'}`} />
                    {t('studio.export.title')}
                  </h2>
                  <StatusBadge status={workflowState.export} color="cyan" activeLabel={t('studio.export.exporting')} />
                </>
              }
            >
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-[11px] text-slate-600 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.platform')}</span>
                    <span className="font-semibold text-slate-800">{exportTemplatePlatform || '—'}</span>
                  </div>
                  {exportTemplateCategory && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('studio.export.category')}</span>
                      <span className="font-semibold text-slate-800">{exportTemplateCategory}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.format')}</span>
                    <span className="font-semibold text-slate-800">{exportFormat || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.version')}</span>
                    <span className="font-semibold text-slate-800">{exportTemplateVersion || '—'}</span>
                  </div>
                  {exportTemplateKey && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('studio.export.templateKey')}</span>
                      <span className="font-semibold text-slate-800">{exportTemplateKey}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.columns')}</span>
                    <span className="font-semibold text-slate-800">{exportColumnCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.rows')}</span>
                    <span className="font-semibold text-slate-800">{exportRowCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t('studio.export.validation')}</span>
                    <span className={`font-semibold ${exportValidationPassed ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {exportValidationPassed ? t('studio.export.validationPass') : t('studio.export.validationPending')}
                    </span>
                  </div>
                  {exportFileName && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">{t('studio.export.fileName')}</span>
                      <span className="font-semibold text-slate-800 truncate">{exportFileName}</span>
                    </div>
                  )}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('studio.export.delivery')}</div>
                    <div className="mt-2 text-[11px] text-slate-600">
                      {exportDownloadUrl ? t('studio.export.ready') : t('studio.export.pending')}
                    </div>
                  </div>
                  {exportDownloadUrl ? (
                    <a
                      href={exportDownloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-[11px] font-semibold text-white hover:bg-cyan-700"
                    >
                      <CloudDownload style={{ fontSize: 14 }} />
                      {t('studio.export.download')}
                    </a>
                  ) : (
                    <div className="text-[10px] text-slate-400">{t('studio.export.waiting')}</div>
                  )}
                </div>
                {exportValidationErrors.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[10px] text-amber-700 lg:col-span-2">
                    <div className="font-bold">{t('studio.export.validationErrors')}</div>
                    <ul className="mt-2 list-disc list-inside space-y-1">
                      {exportValidationErrors.slice(0, 5).map((item: any, index: number) => (
                        <li key={`${item.column ?? 'col'}-${index}`}>
                          {item.column ? `${item.column}: ` : ''}{item.message ?? ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AgentModule>

          </div>
        </section>

        {/* Right Sidebar: Neural Insight Center */}
        <aside className="w-80 border-l border-slate-200 bg-white hidden xl:flex flex-col overflow-y-auto flex-shrink-0 relative z-40 shrink-0">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-900">{t('studio.agentLogs.title')}</h3>
            </div>
            <MoreVert className="text-slate-400 text-sm cursor-pointer" />
          </div>
          <div className="px-4 py-3">
            <button className="w-full py-2 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors shadow-sm">
              <Hub style={{ fontSize: 14 }} />
              {t('studio.agentLogs.subtitle')}
            </button>
          </div>
          <div className="px-4 space-y-3 pb-6">
            {agentStageList.map((agent) => {
              const statusMeta = resolveAgentStatusMeta(agent.status);
              const isActive = expandedAgentStages.has(agent.key);
              const run = agentRunMap.get(agent.key);
              const eventCount = run?.events.length ?? 0;
              const runStatusMeta = resolveRunStatusMeta(run?.status);
              const lastEventTime = run?.events?.[run.events.length - 1]?.created_at ?? run?.completed_at ?? run?.started_at ?? null;
              const lastUpdated = formatAgentTime(lastEventTime);
              const durationLabel = formatAgentDuration(run?.started_at ?? null, run?.completed_at ?? null);
              const displayEvents = run?.events.slice(-50) ?? [];
              return (
                <div key={agent.key} className="space-y-2">
                  <button
                    onClick={() => toggleAgentStage(agent.key)}
                    className={`w-full text-left px-3 py-2 rounded-xl border transition-all flex items-center justify-between group ${
                      isActive ? 'border-emerald-200 bg-emerald-50/70 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${isActive ? 'border-emerald-200 bg-white' : 'border-slate-100 bg-slate-50'}`}>
                        {agent.icon}
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-800">{agent.label}</div>
                        <div className={`text-[9px] font-bold uppercase tracking-wider ${statusMeta.className}`}>{statusMeta.label}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-semibold text-slate-400">{t('studio.agentLogs.logCount', { count: eventCount })}</span>
                      <div className={`px-2 py-1 rounded-full text-[8px] font-bold uppercase border ${statusMeta.badge} ${statusMeta.className}`}>
                        {statusMeta.label}
                      </div>
                      <div className={`transition-transform duration-200 ${isActive ? 'rotate-180 text-emerald-500' : 'text-slate-300 group-hover:text-slate-500'}`}>
                        <ExpandMore style={{ fontSize: 18 }} />
                      </div>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out origin-top ${
                      isActive ? 'max-h-[400px] opacity-100 translate-y-0 scale-y-100' : 'max-h-0 opacity-0 -translate-y-1 scale-y-95'
                    }`}
                  >
                    <div className="relative rounded-xl border border-slate-800 bg-[#0B1220] text-slate-200 p-4 shadow-inner overflow-hidden">
                      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_60%)]"></div>
                      <div className="relative flex items-center justify-between">
                        <div>
                          <span className="text-[9px] uppercase tracking-[0.2em] text-slate-400">{t('studio.agentLogs.chainTitle')}</span>
                          {lastUpdated && (
                            <div className="text-[9px] text-slate-500 mt-1">{t('studio.agentLogs.lastUpdated', { time: lastUpdated })}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {durationLabel && (
                            <span className="text-[9px] text-slate-400">{t('studio.agentLogs.duration', { duration: durationLabel })}</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[8px] font-bold uppercase border ${runStatusMeta.badge} ${runStatusMeta.className}`}>
                            {runStatusMeta.label}
                          </span>
                          {run?.status === 'running' && (
                            <span className="text-[9px] font-semibold text-emerald-400 flex items-center gap-1 animate-pulse">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                              {t('studio.agentLogs.streaming')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        ref={(node) => {
                          agentLogRefs.current[agent.key] = node;
                        }}
                        className="relative mt-4 space-y-2 max-h-[260px] overflow-y-auto pr-1 text-[10px] font-mono"
                      >
                        <div className="absolute left-[30px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-slate-700/70 to-transparent"></div>
                        {agentLogError && (
                          <div className="text-[10px] text-red-400">{agentLogError}</div>
                        )}
                        {!agentLogError && (!run || run.events.length === 0) && (
                          <div className="flex flex-col items-center justify-center gap-3 py-6 text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                              <SmartToy className="text-slate-400" style={{ fontSize: 16 }} />
                            </div>
                            <div className="text-center">
                              <div className="text-[11px] font-semibold text-slate-300">{t('studio.agentLogs.emptyTitle')}</div>
                              <div className="text-[9px] text-slate-500 mt-1">{t('studio.agentLogs.emptyDesc')}</div>
                            </div>
                          </div>
                        )}
                        {displayEvents.map((event, index) => {
                          const eventLabel = agentEventLabelMap[event.event_type as keyof typeof agentEventLabelMap] || event.event_type;
                          const timeLabel = formatAgentTime(event.created_at ?? undefined) || `#${event.seq}`;
                          const fullMessage = event.message?.trim() || '';
                          const summaryMessage = fullMessage.replace(/\s+/g, ' ').trim() || eventLabel;
                          const eventKey = `${agent.key}-${event.event_type}-${event.seq}`;
                          const isExpandedLog = expandedLogEntries.has(eventKey);
                          const canExpand = fullMessage.length > summaryMessage.length || fullMessage.includes('\n');
                          const isNewLog = !animatedLogKeysRef.current.has(eventKey);
                          if (isNewLog) {
                            animatedLogKeysRef.current.add(eventKey);
                          }
                          const shouldAnimate = isNewLog && run?.status === 'running';
                          const renderHighlightedText = (value: string) => {
                            const parts = splitLogMessageParts(value);
                            return parts.map((part, partIndex) => {
                              if (!part.highlight) {
                                return <span key={`${eventKey}-part-${partIndex}`}>{part.text}</span>;
                              }
                              const keyword = part.text.toUpperCase();
                              const keywordClass =
                                keyword === 'ERROR'
                                  ? 'text-red-300'
                                  : keyword === 'CACHE'
                                  ? 'text-amber-300'
                                  : 'text-purple-300';
                              return (
                                <span key={`${eventKey}-part-${partIndex}`} className={`font-semibold ${keywordClass}`}>
                                  {part.text}
                                </span>
                              );
                            });
                          };
                          return (
                            <div
                              key={eventKey}
                              className="flex flex-col gap-1 text-[10px] animate-in fade-in slide-in-from-top-1 duration-200"
                            >
                              <div className="flex items-start gap-2">
                              <span className="text-slate-500 min-w-[54px]">{timeLabel}</span>
                              <span className={`${resolveEventColor(event.event_type)} uppercase tracking-wider text-[9px] min-w-[70px]`}>
                                {eventLabel}
                              </span>
                              <div className="flex-1 flex items-start gap-2 min-w-0">
                                <span className={`mt-1 h-2 w-2 rounded-full ${resolveEventDot(event.event_type)}`}></span>
                                <span
                                  className={`text-slate-200 leading-relaxed truncate ${
                                    event.event_type === 'thinking' ? 'italic text-slate-300' : ''
                                  } ${shouldAnimate ? 'agent-log-typing' : ''}`}
                                  style={shouldAnimate ? { animationDelay: `${Math.min(index * 0.06, 0.6)}s` } : undefined}
                                >
                                  {renderHighlightedText(summaryMessage)}
                                </span>
                              </div>
                              {canExpand && (
                                <button
                                  type="button"
                                  onClick={() => toggleLogEntry(eventKey)}
                                  className="text-[9px] text-slate-400 hover:text-slate-200 flex items-center gap-1"
                                >
                                  {isExpandedLog ? t('studio.agentLogs.collapseLog') : t('studio.agentLogs.expandLog')}
                                  {isExpandedLog ? <UnfoldLess style={{ fontSize: 14 }} /> : <UnfoldMore style={{ fontSize: 14 }} />}
                                </button>
                              )}
                              </div>
                              {isExpandedLog && (
                                <div className="ml-[86px] mr-2 rounded-lg border border-slate-700/80 bg-slate-900/60 px-3 py-2 text-[10px] text-slate-200">
                                  <div className="whitespace-pre-wrap leading-relaxed">{renderHighlightedText(fullMessage)}</div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </main>

      {/* Footer */}
      <footer className="h-16 bg-white/95 backdrop-blur-md border-t border-slate-200 flex items-center px-8 justify-between z-50">
        <div className="flex items-center gap-6">
          <div className="flex -space-x-2">
            <div className="w-7 h-7 rounded-full border-2 border-white bg-blue-50 flex items-center justify-center shadow-sm">
              <Search style={{ fontSize: 12 }} className="text-blue-500" />
            </div>
            <div className="w-7 h-7 rounded-full border-2 border-white bg-blue-50 flex items-center justify-center shadow-sm">
              <Visibility style={{ fontSize: 12 }} className="text-blue-500" />
            </div>
            <div className="w-7 h-7 rounded-full border-2 border-white bg-primary flex items-center justify-center shadow-sm animate-pulse">
              <EditNote style={{ fontSize: 12 }} className="text-white" />
            </div>
          </div>
          <span className="text-[11px] text-slate-500 font-medium hidden sm:inline-block">{t('ai.neuralActive')}</span>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-5 py-2 rounded-lg border border-slate-200 font-semibold text-xs hover:bg-slate-50 transition-all text-slate-700">
            {t('ai.reviewChanges')}
          </button>
          <button
            onClick={handleGenerate}
            className={`px-6 py-2 rounded-lg text-white font-bold text-xs shadow-lg flex items-center gap-2 transition-all ${
              !resolvedCollectionId || isGenerating ? 'bg-slate-300 cursor-not-allowed' : 'bg-primary hover:bg-blue-700 shadow-primary/20'
            }`}
            disabled={!resolvedCollectionId || isGenerating}
          >
            <RocketLaunch style={{ fontSize: 14 }} className="text-white" />
            {isGenerating ? t('studio.control.starting') : t('ai.oneClick')}
          </button>
        </div>
      </footer>
    </div>
  );
};

// --- Sub-components ---

const SidebarButton: React.FC<{ icon: React.ReactNode; label: string; isOpen: boolean; active?: boolean }> = ({ icon, label, isOpen, active }) => {
  return (
    <button className={`flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-slate-50 transition-all group w-full ${active ? 'bg-blue-50/50' : ''} ${!isOpen ? 'justify-center' : ''}`}>
      <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-white border shadow-sm transition-all shrink-0 ${active ? 'border-blue-200 shadow-blue-100' : 'border-slate-100 group-hover:border-slate-200 group-hover:shadow-md'}`}>
        {icon}
      </div>
      <div className={`transition-all duration-300 overflow-hidden whitespace-nowrap ${isOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
        <span className={`text-xs font-bold ${active ? 'text-primary' : 'text-slate-600 group-hover:text-slate-900'}`}>{label}</span>
      </div>
      {!isOpen && (
        <div className="absolute left-16 px-2 py-1 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
          {label}
        </div>
      )}
    </button>
  );
};

const StatusBadge: React.FC<{ status: AgentStatus; color: 'blue' | 'amber' | 'red' | 'emerald' | 'cyan' | 'pink'; activeLabel?: string }> = ({ status, color, activeLabel }) => {
  const { t } = useLanguage();
  const getBg = () => {
    if (status === 'COMPLETE') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (status === 'ACTIVE' || status === 'THINKING') {
      switch (color) {
        case 'blue': return 'bg-blue-500 text-white border-blue-600';
        case 'amber': return 'bg-amber-500 text-white border-amber-600';
        case 'red': return 'bg-red-500 text-white border-red-600';
        case 'emerald': return 'bg-emerald-500 text-white border-emerald-600';
        case 'cyan': return 'bg-cyan-500 text-white border-cyan-600';
        case 'pink': return 'bg-pink-500 text-white border-pink-600';
        default: return 'bg-slate-500 text-white border-slate-600';
      }
    }
    if (status === 'ERROR') return 'bg-red-500 text-white border-red-600 animate-pulse';
    return 'bg-slate-100 text-slate-400 border-slate-200';
  };

  const getLabel = () => {
    if (status === 'COMPLETE') return t('studio.status.complete');
    if (status === 'THINKING') return t('studio.status.thinking');
    if (status === 'ACTIVE') return activeLabel || t('studio.status.processing');
    if (status === 'ERROR') return t('studio.status.error');
    return t('studio.status.waiting');
  };

  return (
    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase transition-all duration-300 ${getBg()}`}>
      {getLabel()}
    </span>
  );
};

const KeywordTag: React.FC<{ label: string; type: string; active: boolean; delay: number }> = ({ label, type, active, delay }) => (
  <div className={`flex items-center justify-between p-2 bg-white rounded border border-slate-200 shadow-sm transition-all duration-500 ${active ? 'opacity-100 translate-y-0' : 'opacity-50 translate-y-2'}`} style={{ transitionDelay: `${delay}ms` }}>
    <span className="text-[10px] font-semibold">{label}</span>
    <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold uppercase ${type === 'Core' ? 'bg-blue-50 text-blue-600' : type === 'Hot' ? 'bg-orange-50 text-orange-600' : 'bg-purple-50 text-purple-600'}`}>
      {type}
    </span>
  </div>
);

const PromptLine: React.FC<{ slot: number; text: string; reveal: boolean; typing?: boolean }> = ({ slot, text, reveal, typing }) => (
  <div className={`p-1.5 bg-white border border-amber-100 rounded text-[10px] font-mono text-slate-600 transition-all duration-500 ${reveal ? 'opacity-100' : 'opacity-40'}`}>
    <span className="text-amber-500 font-bold mr-1">Slot {slot}:</span>
    {reveal ? text : typing ? <span className="animate-pulse">Generating...|</span> : 'Waiting for context...'}
  </div>
);

const AgentCard: React.FC<{ title: string; status: AgentStatus; icon: React.ReactNode }> = ({ title, status, icon }) => {
  const { t } = useLanguage();
  const getRingColor = () => {
    if (status === 'COMPLETE') return '#10B981'; // Green for complete
    if (status === 'ACTIVE' || status === 'THINKING') return '#3B82F6'; // Blue for active
    if (status === 'ERROR') return '#EF4444'; // Red for error
    return '#E2E8F0'; // Slate for idle
  };

  return (
    <div className={`bg-white rounded-xl p-3 border transition-all duration-500 ${status === 'ERROR' ? 'border-red-200 shadow-md ring-1 ring-red-100 bg-red-50/20' :
      status === 'ACTIVE' || status === 'THINKING' ? 'border-primary/50 shadow-md ring-1 ring-primary/20 scale-[1.02]' :
        status === 'COMPLETE' ? 'border-emerald-100 shadow-sm' :
          'border-slate-200 shadow-sm opacity-80'
      }`}>
      <div className="flex items-start gap-3">
        <div className="relative w-9 h-9 flex items-center justify-center shrink-0">
          <svg className={`absolute inset-0 w-full h-full -rotate-90 transition-all duration-500 ${status !== 'IDLE' ? 'opacity-100' : 'opacity-20'}`}>
            <circle cx="18" cy="18" fill="none" r="16" stroke="#E2E8F0" strokeWidth="2"></circle>
            <circle
              cx="18" cy="18" fill="none" r="16" stroke={getRingColor()}
              strokeDasharray="100" strokeDashoffset={status === 'COMPLETE' ? 0 : status === 'ACTIVE' || status === 'THINKING' ? 25 : status === 'ERROR' ? 0 : 100}
              strokeWidth="2"
              className={status === 'ACTIVE' || status === 'THINKING' ? 'animate-[spin_2s_linear_infinite]' : 'transition-all duration-1000'}
            ></circle>
          </svg>
          {(status === 'ACTIVE' || status === 'THINKING') && (
            <div className="absolute inset-0 bg-blue-400/20 rounded-full blur-md animate-pulse"></div>
          )}
          {status === 'ERROR' && (
            <div className="absolute inset-0 bg-red-400/30 rounded-full blur-md animate-pulse"></div>
          )}
          <div className="relative z-10 text-slate-500">
            {status === 'ERROR' ? <div className="text-red-500 font-bold text-xs">!</div> : icon}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-0.5">
            <p className={`text-[11px] font-bold transition-colors ${status === 'COMPLETE' ? 'text-emerald-700' : status === 'ERROR' ? 'text-red-700' : 'text-slate-900'}`}>{title}</p>
            <span className={`text-[9px] font-bold ${status === 'COMPLETE' ? 'text-emerald-500' :
              status === 'ACTIVE' || status === 'THINKING' ? 'text-blue-500' :
                status === 'ERROR' ? 'text-red-500' :
                  'text-slate-400'
              }`}>
              {status === 'COMPLETE'
                ? t('studio.status.complete')
                : status === 'IDLE'
                  ? t('studio.status.waiting')
                  : status === 'ERROR'
                    ? t('studio.status.error')
                    : t('studio.status.processing')}
            </span>
          </div>
          {status === 'ERROR' ? (
            <div className="text-[10px] text-red-500 font-mono mt-1 leading-tight">
              {t('studio.agent.error')}
            </div>
          ) : (
            <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden mt-1">
              <div className={`h-full transition-all duration-500 ${status === 'COMPLETE' ? 'w-full bg-emerald-500' : status === 'ACTIVE' || status === 'THINKING' ? 'w-2/3 bg-blue-500 animate-[shimmer_1s_infinite]' : 'w-0'}`}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
